/**
 * Operator progress — projects, contacts, minutes, tasks, attachments, surveillances.
 */

import { importsForOperator } from "./operator-progress-import-data.mjs";
import { renderAzulTrackerDashboard } from "./operator-progress-azul-tracker.mjs";
import { buildAzulCallMinutes } from "./operator-progress-azul-minutes.mjs";

export const TASK_STATUSES = [
  { value: "not_due_yet", label: "Not due yet", variant: "gray" },
  { value: "not_started", label: "Not started", variant: "blue" },
  { value: "in_progress", label: "In progress", variant: "amber" },
  { value: "closed", label: "Closed", variant: "green" },
  { value: "cancelled", label: "Cancelled", variant: "red" },
];

export const TASK_PRIORITIES = [
  { value: "low", label: "Low", variant: "gray" },
  { value: "normal", label: "Normal", variant: "blue" },
  { value: "high", label: "High", variant: "red" },
];

export const MINUTE_STATUSES = [
  { value: "open", label: "Open", variant: "blue" },
  { value: "closed", label: "Closed", variant: "green" },
];

export const MINUTE_STATUS_FILTERS = [
  { value: "open", label: "Open only" },
  { value: "closed", label: "Closed only" },
  { value: "all", label: "All" },
];

export const PROJECT_TYPES = [
  { value: "general", label: "General" },
  { value: "surveillance", label: "Surveillance" },
  { value: "engine_exchange", label: "Engine exchange" },
  { value: "redelivery", label: "Redelivery" },
  { value: "claim", label: "Claim" },
];

const OPEN_SURV_STATUSES = new Set(["completed", "cancelled"]);

export function newId(prefix) {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export const AZUL_MINUTES_REVISION = 7;

const TRACKER_STATUSES = [
  { value: "IN PROGRESS", label: "In progress", variant: "green" },
  { value: "PENDING", label: "Pending", variant: "amber" },
  { value: "CLOSED", label: "Closed", variant: "gray" },
];

const TRACKER_PRIORITIES = [
  { value: "NORMAL", label: "Normal", variant: "gray" },
  { value: "CRITICAL", label: "Critical", variant: "red" },
];

export function createBlankProject() {
  return normaliseProject({
    id: newId("proj"),
    title: "New project",
    type: "general",
    summary: "",
    criticalItems: [],
    contacts: [],
    minutes: [],
    tasks: [],
    attachments: [],
  });
}

export function ensureProjectsStore(store) {
  if (!store || typeof store !== "object") return store;
  if (!Array.isArray(store.projects)) store.projects = [];
  if (store.version == null || store.version < 2) store.version = 2;
  for (const p of store.projects) normaliseProject(p);
  return store;
}

function taskText(value) {
  return String(value ?? "").trim();
}

/** Next hierarchical task number under a parent (max sibling index + 1, not array length). */
function nextTaskNumber(parentNumber, tasks) {
  const pNum = taskText(parentNumber);
  const prefix = pNum ? `${pNum}.` : "";
  let maxIndex = 0;
  for (const t of tasks || []) {
    if (taskText(t.parentNumber) !== pNum) continue;
    const num = String(t.number || "").trim();
    if (!num) continue;
    if (pNum) {
      if (!num.startsWith(prefix)) continue;
      const seg = parseInt(num.slice(prefix.length).split(".")[0], 10);
      if (Number.isFinite(seg)) maxIndex = Math.max(maxIndex, seg);
    } else {
      const seg = parseInt(num.split(".")[0], 10);
      if (Number.isFinite(seg)) maxIndex = Math.max(maxIndex, seg);
    }
  }
  return `${prefix}${maxIndex + 1}`;
}

/** Section (topic) number for a principal topic — prefer explicit sectionNumber, else root of #. */
function sectionNumberForTopic(minutes, principalTopic) {
  const siblings = (minutes || []).filter((m) => (m.principalTopic || "General") === principalTopic);
  for (const m of siblings) {
    const sn = String(m.sectionNumber || "").trim();
    if (/^\d+$/.test(sn)) return sn;
  }
  for (const m of siblings) {
    const n = String(m.number || "").trim();
    if (/^\d+$/.test(n)) return n;
  }
  const counts = new Map();
  for (const m of siblings) {
    const root = String(m.number || "").trim().match(/^(\d+)/)?.[1];
    if (root) counts.set(root, (counts.get(root) || 0) + 1);
  }
  let best = "";
  let bestN = 0;
  for (const [root, n] of counts) {
    if (n > bestN || (n === bestN && Number(root) < Number(best || Infinity))) {
      best = root;
      bestN = n;
    }
  }
  return best;
}

/** Next free top-level topic number across all minutes. */
function nextTopLevelSection(minutes) {
  let max = 0;
  for (const m of minutes || []) {
    const sn = String(m.sectionNumber || "").trim();
    if (/^\d+$/.test(sn)) max = Math.max(max, Number(sn));
    const root = String(m.number || "").trim().match(/^(\d+)/)?.[1];
    if (root) max = Math.max(max, Number(root));
  }
  return String(max + 1);
}

/** Next child number under a section (8.1, 8.2, …) — uses max index, not sibling count. */
function nextMinuteChildNumber(minutes, principalTopic, sectionNumber) {
  const sec = String(sectionNumber || "").trim();
  if (!sec) return "1.1";
  const prefix = `${sec}.`;
  let maxIndex = 0;
  for (const m of minutes || []) {
    if ((m.principalTopic || "General") !== principalTopic) continue;
    const num = String(m.number || "").trim();
    if (num === sec) continue;
    if (!num.startsWith(prefix)) continue;
    const seg = parseInt(num.slice(prefix.length).split(".")[0], 10);
    if (Number.isFinite(seg)) maxIndex = Math.max(maxIndex, seg);
  }
  return `${sec}.${maxIndex + 1}`;
}

/**
 * Fix misnumbered children (e.g. 10.2 under topic 8) and promote a lone "8" row
 * to 8.1 when siblings already exist. Returns true if anything changed.
 */
function repairMinuteNumbering(minutes) {
  if (!Array.isArray(minutes) || !minutes.length) return false;
  const byTopic = new Map();
  for (const m of minutes) {
    const key = m.principalTopic || "General";
    if (!byTopic.has(key)) byTopic.set(key, []);
    byTopic.get(key).push(m);
  }
  let changed = false;
  for (const [topic, items] of byTopic) {
    const sec = sectionNumberForTopic(minutes, topic);
    if (!sec) continue;
    const hasExact = items.some((m) => String(m.number || "").trim() === sec);
    const hasChildren = items.some((m) => String(m.number || "").trim().startsWith(`${sec}.`));
    let badRoot = false;
    for (const m of items) {
      const root = String(m.number || "").trim().match(/^(\d+)/)?.[1];
      if (root && root !== sec) badRoot = true;
      if (String(m.sectionNumber || "").trim() !== sec) {
        m.sectionNumber = sec;
        changed = true;
      }
    }
    const needsRenumber = badRoot || (hasExact && (hasChildren || items.length > 1));
    if (!needsRenumber) continue;
    items.sort((a, b) => compareItemNumbers(a.number, b.number));
    let i = 1;
    for (const m of items) {
      const want = `${sec}.${i}`;
      if (String(m.number || "").trim() !== want) {
        m.number = want;
        changed = true;
      }
      m.sectionNumber = sec;
      i += 1;
    }
  }
  return changed;
}

/** Imported Azul tasks often reference category numbers (e.g. parent "2") without a category row. */
function repairTaskHierarchy(tasks) {
  if (!Array.isArray(tasks) || !tasks.length) return;
  const byNum = new Map(tasks.map((t) => [taskText(t.number), t]).filter(([n]) => n));
  const ensureCategory = (num) => {
    if (!num || byNum.has(num)) return;
    const row = {
      id: newId("tsk"),
      number: num,
      parentNumber: "",
      title: "",
      asset: "",
      owner: "",
      status: "not_started",
      priority: "normal",
      startDate: "",
      targetDate: "",
      dependsOn: [],
      linkedMinuteId: null,
      comments: [],
    };
    tasks.push(row);
    byNum.set(num, row);
  };
  for (const t of tasks) {
    let p = taskText(t.parentNumber);
    while (p && !byNum.has(p)) {
      ensureCategory(p);
      p = taskText(byNum.get(p)?.parentNumber);
    }
  }
}

function normaliseProject(p) {
  if (!p || typeof p !== "object") return p;
  if (!p.id) p.id = newId("proj");
  if (!Array.isArray(p.criticalItems)) p.criticalItems = [];
  if (!Array.isArray(p.contacts)) p.contacts = [];
  if (!Array.isArray(p.minutes)) p.minutes = [];
  if (!Array.isArray(p.tasks)) p.tasks = [];
  if (!Array.isArray(p.attachments)) p.attachments = [];
  for (const c of p.contacts) if (!c.id) c.id = newId("ct");
  for (const m of p.minutes) {
    if (!m.id) m.id = newId("min");
    if (!Array.isArray(m.comments)) m.comments = [];
    if (!m.status) m.status = "open";
    ensureMinuteTracker(m);
  }
  repairMinuteNumbering(p.minutes);
  for (const t of p.tasks) {
    if (!t.id) t.id = newId("tsk");
    if (!Array.isArray(t.comments)) t.comments = [];
    if (!Array.isArray(t.dependsOn)) t.dependsOn = [];
  }
  for (const a of p.attachments) {
    if (!a.id) a.id = newId("att");
    if (!a.createdAt) a.createdAt = new Date().toISOString();
    if (!Array.isArray(a.comments)) a.comments = [];
  }
  repairTaskHierarchy(p.tasks);
  return p;
}

export function activeProject(store, projectId) {
  ensureProjectsStore(store);
  const list = store.projects || [];
  if (!list.length) return null;
  if (projectId) {
    const hit = list.find((p) => p.id === projectId);
    if (hit) return hit;
  }
  if (store.activeProjectId) {
    const hit = list.find((p) => p.id === store.activeProjectId);
    if (hit) return hit;
  }
  return list[0];
}

function ensureSortState(obj, key, dir = "asc") {
  if (!obj || typeof obj !== "object") return { key, dir };
  if (!obj.key) obj.key = key;
  if (!obj.dir) obj.dir = dir;
  return obj;
}

/** Per-project UI prefs (task schedule, table sorts) — isolated per project. */
export function prefsForProject(store, projectId) {
  if (!projectId) return {};
  if (!store.uiPrefs) store.uiPrefs = {};
  if (!store.uiPrefs.byProject) store.uiPrefs.byProject = {};
  // One-shot: meeting minutes default presentation is numbered 1…N (not A–Z by title).
  if (store.uiPrefs._minutesNumberOrderMigrated !== true) {
    for (const pref of Object.values(store.uiPrefs.byProject)) {
      if (pref?.minutesSort?.key === "principal" || pref?.minutesSort?.key === "principalTopic") {
        pref.minutesSort.key = "number";
        pref.minutesSort.dir = "asc";
      }
    }
    store.uiPrefs._minutesNumberOrderMigrated = true;
  }
  let p = store.uiPrefs.byProject[projectId];
  if (!p) {
    p = {};
    store.uiPrefs.byProject[projectId] = p;
  }
  if (!p.taskSort) {
    p.taskSort = store.uiPrefs.taskSort
      ? { ...store.uiPrefs.taskSort }
      : { key: "number", dir: "asc" };
  }
  if (!p.taskFilter) {
    p.taskFilter = store.uiPrefs.taskFilter
      ? { ...store.uiPrefs.taskFilter, text: store.uiPrefs.taskFilter.text || "" }
      : { status: "all", priority: "all", text: "" };
  }
  if (!p.taskCollapsed) {
    p.taskCollapsed = Array.isArray(store.uiPrefs.taskCollapsed)
      ? [...store.uiPrefs.taskCollapsed]
      : [];
  }
  if (!p.minutesSort) p.minutesSort = {};
  if (!p.contactsSort) p.contactsSort = {};
  if (!p.attachmentsSort) p.attachmentsSort = {};
  ensureSortState(p.minutesSort, "number");
  ensureSortState(p.contactsSort, "lastName");
  ensureSortState(p.attachmentsSort, "label");
  return p;
}

function compareSortValues(av, bv, dir = 1) {
  if (typeof av === "number" && typeof bv === "number" && !Number.isNaN(av) && !Number.isNaN(bv)) {
    return (av - bv) * dir;
  }
  return String(av ?? "").localeCompare(String(bv ?? ""), undefined, { numeric: true, sensitivity: "base" }) * dir;
}

function sortItems(items, sortState, valueFn) {
  const key = sortState?.key;
  if (!key) return items;
  const dir = sortState?.dir === "desc" ? -1 : 1;
  return [...items].sort((a, b) => compareSortValues(valueFn(a, key), valueFn(b, key), dir));
}

function toggleSortState(sortState, colKey) {
  if (!sortState || typeof sortState !== "object") return;
  if (sortState.key === colKey) sortState.dir = sortState.dir === "asc" ? "desc" : "asc";
  else {
    sortState.key = colKey;
    sortState.dir = "asc";
  }
}

function contactQuality(contacts) {
  return (contacts || []).filter((c) => /@/.test(String(c?.email || ""))).length;
}

function repairProjectFromImport(existing, imported) {
  if (!existing || !imported) return false;
  let changed = false;
  if (contactQuality(imported.contacts) > contactQuality(existing.contacts)) {
    existing.contacts = JSON.parse(JSON.stringify(imported.contacts));
    changed = true;
  }
  const badAttLabel = (a) => a?.kind === "link" && /^https?:\/\//i.test(String(a?.label || ""));
  const existingBad = (existing.attachments || []).filter(badAttLabel).length;
  const importGood = (imported.attachments || []).filter((a) => a?.label && !badAttLabel(a)).length;
  if (importGood > 0 && (existingBad > 0 || !(existing.attachments || []).length)) {
    existing.attachments = JSON.parse(JSON.stringify(imported.attachments));
    changed = true;
  }
  return changed;
}

export function applyImportBundles(store, operatorName) {
  ensureProjectsStore(store);
  if (!Array.isArray(store._importedBundles)) store._importedBundles = [];
  const bundles = importsForOperator(operatorName);
  let added = 0;
  for (const bundle of bundles) {
    const title = bundle.project?.title || "project";
    const key = `${title}:${bundle.project?.id || ""}`;
    const clone = JSON.parse(JSON.stringify(bundle.project));
    normaliseProject(clone);
    const existing = store.projects.find((p) => p.title === clone.title);
    const exists = Boolean(existing);
    if (exists) repairProjectFromImport(existing, clone);
    const marked = store._importedBundles.includes(key);
    if (marked && exists) continue;
    if (!exists) {
      store.projects.push(clone);
      added += 1;
    }
    if (!marked) store._importedBundles.push(key);
  }
  if ((added || !store.activeProjectId) && store.projects[0]) {
    store.activeProjectId = store.activeProjectId || store.projects[0].id;
  }
  refreshAzulMinutesIfNeeded(store, operatorName);
  return added;
}

function refreshAzulMinutesIfNeeded(store, operatorName) {
  const n = String(operatorName || "").trim().toLowerCase();
  if (!n.includes("azul")) return;
  const revision = Number(store._azulMinutesRevision) || 0;
  if (revision >= AZUL_MINUTES_REVISION) return;

  let proj = store.projects.find((p) => p.title === "DAE Technical Call Tracker");
  if (!proj) {
    proj = createBlankProject();
    proj.title = "DAE Technical Call Tracker";
    proj.type = "claim";
    store.projects.push(proj);
  }
  // Replace meeting minutes with the current call seed; drop legacy progress-tracker blob.
  proj.minutes = buildAzulCallMinutes();
  if (proj.azulTracker) delete proj.azulTracker;
  normaliseProject(proj);

  if (!store.visibleWidgets || typeof store.visibleWidgets !== "object") store.visibleWidgets = {};
  // One-shot defaults only — never override after the user configures widgets.
  if (store._widgetsUserConfigured !== true) {
    store.visibleWidgets.minutes = true;
    store.visibleWidgets.projects = true;
    store.visibleWidgets.azulTracker = false;
    store._autoEnabledMinutes = true;
  }

  store._azulMinutesRevision = AZUL_MINUTES_REVISION;
  store._pendingAutoEnablePersist = true;
}

export function parseDateMs(value) {
  const t = String(value || "").trim();
  if (!t) return null;
  const direct = Date.parse(t);
  if (Number.isFinite(direct)) return direct;
  const months = {
    jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
    jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
  };
  // 13-Jan-2024 / 13-JAN-24
  let m = t.match(/^(\d{1,2})-([A-Za-z]{3})-(\d{2,4})$/);
  if (m) {
    const mon = months[m[2].toLowerCase()];
    let year = Number(m[3]);
    if (year < 100) year += 2000;
    if (mon == null) return null;
    return Date.UTC(year, mon, Number(m[1]));
  }
  // 13-Jan / 13 JAN (no year — assume current, or previous if date would be in the future)
  m = t.match(/^(\d{1,2})[-\s]([A-Za-z]{3})$/);
  if (m) {
    const mon = months[m[2].toLowerCase()];
    if (mon == null) return null;
    const day = Number(m[1]);
    const now = new Date();
    let year = now.getUTCFullYear();
    let ms = Date.UTC(year, mon, day);
    if (ms > Date.now() + 14 * 864e5) ms = Date.UTC(year - 1, mon, day);
    return ms;
  }
  // 2024-01-13
  m = t.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return null;
}

const MONTHS_MMM = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Calendar Y/M/D from a chronology date string (house formats use the written day, not TZ). */
export function chronologyDateParts(value) {
  const t = String(value || "").trim();
  if (!t) return null;
  const months = {
    jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
    jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
  };
  let m = t.match(/^(\d{1,2})-([A-Za-z]{3})-(\d{2,4})$/);
  if (m) {
    const mon = months[m[2].toLowerCase()];
    let year = Number(m[3]);
    if (year < 100) year += 2000;
    if (mon == null) return null;
    return { y: year, m: mon, d: Number(m[1]) };
  }
  m = t.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return { y: Number(m[1]), m: Number(m[2]) - 1, d: Number(m[3]) };
  const ms = parseDateMs(t);
  if (ms == null) return null;
  const dt = new Date(ms);
  return { y: dt.getUTCFullYear(), m: dt.getUTCMonth(), d: dt.getUTCDate() };
}

/** Chronology / print date: 26-Aug-26 */
export function formatDdMmmYy(value) {
  const p = chronologyDateParts(value);
  if (!p || p.m < 0 || p.m > 11) return String(value || "").trim();
  return `${String(p.d).padStart(2, "0")}-${MONTHS_MMM[p.m]}-${String(p.y).slice(-2)}`;
}

function todayDdMmmYy(now = new Date()) {
  return `${String(now.getDate()).padStart(2, "0")}-${MONTHS_MMM[now.getMonth()]}-${String(now.getFullYear()).slice(-2)}`;
}

function chronologyToIso(value) {
  const p = chronologyDateParts(value);
  if (!p) return "";
  return `${p.y}-${String(p.m + 1).padStart(2, "0")}-${String(p.d).padStart(2, "0")}`;
}

function chronologyFromIso(iso) {
  const t = String(iso || "").trim();
  if (!t) return "";
  return formatDdMmmYy(t) || t;
}

export function isChronologyToday(value, now = new Date()) {
  const p = chronologyDateParts(value);
  if (!p) return false;
  return p.y === now.getFullYear() && p.m === now.getMonth() && p.d === now.getDate();
}

export function taskDepsMet(task, tasks) {
  const deps = task?.dependsOn || [];
  if (!deps.length) return true;
  const byNum = new Map((tasks || []).map((t) => [String(t.number), t]));
  return deps.every((n) => {
    const dep = byNum.get(String(n));
    return dep && (dep.status === "closed" || dep.status === "cancelled");
  });
}

export function effectiveTaskStatus(task, tasks, todayMs = Date.now()) {
  const stored = String(task?.status || "").toLowerCase();
  if (stored === "closed" || stored === "cancelled" || stored === "in_progress") return stored;
  const startMs = parseDateMs(task?.startDate);
  if (startMs != null && startMs > todayMs) return "not_due_yet";
  if (!taskDepsMet(task, tasks)) return stored === "not_due_yet" ? "not_due_yet" : "not_started";
  if (startMs != null && startMs <= todayMs) return stored || "not_started";
  return stored || "not_started";
}

function statusMeta(value) {
  return TASK_STATUSES.find((s) => s.value === value) || TASK_STATUSES[1];
}

function priorityMeta(value) {
  return TASK_PRIORITIES.find((p) => p.value === value) || TASK_PRIORITIES[1];
}

function sortCommentsNewestFirst(comments) {
  return [...(comments || [])].sort((a, b) => Date.parse(b?.at || 0) - Date.parse(a?.at || 0));
}

function groupMinutes(minutes) {
  const groups = [];
  const map = new Map();
  for (const m of minutes || []) {
    const key = m.principalTopic || "General";
    if (!map.has(key)) {
      const g = { principalTopic: key, items: [] };
      map.set(key, g);
      groups.push(g);
    }
    map.get(key).items.push(m);
  }
  for (const g of groups) {
    g.items.sort((a, b) => String(a.number).localeCompare(String(b.number), undefined, { numeric: true }));
  }
  return groups;
}

function sortedTasks(tasks) {
  return [...(tasks || [])].sort((a, b) =>
    String(a.number).localeCompare(String(b.number), undefined, { numeric: true })
  );
}

function fmtWhen(iso) {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("en-GB", { dateStyle: "medium", timeStyle: "short" }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function isOfficePreview(mime, name) {
  const m = String(mime || "").toLowerCase();
  const n = String(name || "").toLowerCase();
  return m.includes("pdf") || n.endsWith(".pdf") || m.includes("image/") || n.match(/\.(png|jpe?g|gif|webp)$/);
}

function isGoogleOrSharePoint(url) {
  return /docs\.google\.com|drive\.google|sharepoint|1drv\.ms|office\.com/i.test(String(url || ""));
}

/** @param {object} ctx */
export function renderProjectWidgets(ctx) {
  const {
    el,
    cleanText,
    store,
    project,
    setActiveProjectId,
    persist,
    rerender,
    forceRerender,
    sectionHeading,
    progressBadge,
    operatorId,
    fetchJson,
    apiUrl,
    clickEditCell,
    clickEditDateCell,
    sectionVisible,
  } = ctx;

  const show = (id) => (typeof sectionVisible === "function" ? sectionVisible(id) !== false : true);
  if (!store) return null;
  if (!store.uiPrefs || typeof store.uiPrefs !== "object") store.uiPrefs = {};
  if (!store.uiPrefs.minutesFilter) store.uiPrefs.minutesFilter = "open";

  const projects = store.projects || [];
  const projectPrefs = project ? prefsForProject(store, project.id) : null;

  const toolbar = el("div", { class: "bg-white border border-gray-200 rounded-xl p-4" }, [
    el("div", { class: "flex flex-wrap items-end gap-3" }, [
      el("div", { class: "min-w-[200px]" }, [
        el("label", { class: "block text-xs font-medium text-gray-600 mb-1", text: "Project" }),
        el("select", {
          class: "w-full rounded-lg border border-gray-200 px-3 py-2 text-sm",
          onchange: (ev) => {
            const id = cleanText(ev.target.value);
            store.activeProjectId = id;
            setActiveProjectId?.(id);
            ev.target.blur();
            persist();
            (forceRerender || rerender)();
          },
        }, [
          ...(projects.length
            ? [...projects]
                .sort((a, b) => String(a.title || "").localeCompare(String(b.title || ""), undefined, { sensitivity: "base" }))
                .map((p) => el("option", { value: p.id, text: p.title || "Untitled project", selected: p.id === project?.id }))
            : [el("option", { value: "", text: "No projects yet" })]),
        ]),
      ]),
      project
        ? (() => {
            const nameInput = el("input", {
              type: "text",
              class: "w-full min-w-[220px] rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-900 focus:border-dae focus:ring-1 focus:ring-dae/30",
              value: project.title || "",
              placeholder: "Project name",
              "data-op-block-rerender": "1",
              oninput: (ev) => {
                project.title = cleanText(ev.target.value);
                persist();
              },
              onblur: () => {
                if (!String(project.title || "").trim()) project.title = "Untitled project";
                persist();
                (forceRerender || rerender)();
              },
            });
            if (project._focusTitle) {
              delete project._focusTitle;
              setTimeout(() => {
                nameInput.focus();
                nameInput.select();
              }, 0);
            }
            return el("div", { class: "flex-1 min-w-[220px]" }, [
              el("label", { class: "block text-xs font-medium text-gray-600 mb-1", text: "Name" }),
              nameInput,
            ]);
          })()
        : null,
      project
        ? el("button", {
            type: "button",
            class: "px-3 py-2 text-sm font-medium text-red-700 border border-red-200 rounded-lg hover:bg-red-50",
            text: "Delete project",
            onclick: () => {
              if (!window.confirm(`Delete project "${project.title}"?`)) return;
              store.projects = store.projects.filter((p) => p.id !== project.id);
              if (store.uiPrefs?.byProject) delete store.uiPrefs.byProject[project.id];
              store.activeProjectId = store.projects[0]?.id || null;
              setActiveProjectId?.(store.activeProjectId);
              persist();
              (forceRerender || rerender)();
            },
          })
        : null,
    ]),
  ]);

  const projSection = el("div", {
    "data-active-project-id": project?.id || "",
    class: "space-y-6",
    ondragover: (ev) => {
      if (!show("attachments")) return;
      if (!ev.dataTransfer?.types?.includes("Files")) return;
      ev.preventDefault();
      ev.currentTarget.classList.add("ring-2", "ring-dae/30");
    },
    ondragleave: (ev) => { ev.currentTarget.classList.remove("ring-2", "ring-dae/30"); },
    ondrop: async (ev) => {
      ev.currentTarget.classList.remove("ring-2", "ring-dae/30");
      if (!show("attachments") || !project) return;
      const files = [...(ev.dataTransfer?.files || [])];
      if (!files.length) return;
      ev.preventDefault();
      ev.stopPropagation();
      const { operatorId, fetchJson, apiUrl } = ctx;
      if (!operatorId || !fetchJson || !apiUrl) return;
      for (const f of files) {
        const reader = new FileReader();
        await new Promise((resolve) => {
          reader.onload = async () => {
            const resp = await fetchJson(apiUrl(`/api/operator-progress/${operatorId}/attachments`), {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ projectId: project.id, fileName: f.name, mime: f.type || "application/octet-stream", base64: String(reader.result || "") }),
            });
            if (resp?.ok) {
              const att = resp.attachment;
              project.attachments.push({ id: newId("att"), kind: "file", label: f.name, url: "", fileId: att.id, mime: att.mime, createdAt: new Date().toISOString(), comments: [] });
            }
            resolve();
          };
          reader.readAsDataURL(f);
        });
      }
      persist(); rerender();
    },
  });

  projSection.appendChild(toolbar);

  if (!project) {
    projSection.appendChild(el("p", { class: "text-sm text-gray-500", text: "Add a project or select an operator with imported data." }));
    return projSection;
  }

  if (show("azulTracker") && project.azulTracker?.sections?.length) {
    projSection.appendChild(renderAzulTrackerDashboard(ctx, project));
  }

  if (show("projectSummary")) projSection.appendChild(renderProjectHeader(ctx, project));
  if (show("contacts")) projSection.appendChild(renderContactsSection(ctx, project, projectPrefs, persist));
  if (show("minutes")) projSection.appendChild(renderMinutesSection(ctx, project, store.uiPrefs, projectPrefs, persist));
  if (show("tasks")) projSection.appendChild(renderTasksSection(ctx, project, projectPrefs, persist));
  if (show("attachments")) projSection.appendChild(renderAttachmentsSection(ctx, project, projectPrefs, persist));

  return projSection;
}

function truncateText(value, max=64) {
  const t = String(value || "").trim();
  if (!t) return "";
  return t.length > max ? `${t.slice(0, max - 1)}…` : t;
}

function compareItemNumbers(a, b) {
  const as = String(a || "").trim();
  const bs = String(b || "").trim();
  if (!as && !bs) return 0;
  const ap = as.split(".").map((p) => parseInt(p, 10) || 0);
  const bp = bs.split(".").map((p) => parseInt(p, 10) || 0);
  const len = Math.max(ap.length, bp.length);
  for (let i = 0; i < len; i += 1) {
    const av = ap[i] ?? 0;
    const bv = bp[i] ?? 0;
    if (av !== bv) return av - bv;
  }
  return 0;
}

/** When a task number changes, update descendant numbers and parent links. */
function remapTaskNumber(tasks, oldNum, newNum) {
  const oldN = taskText(oldNum);
  const newN = taskText(newNum);
  if (!oldN || oldN === newN) return;
  for (const t of tasks) {
    if (taskText(t.parentNumber) !== oldN) continue;
    const suffix = String(t.number).slice(oldN.length);
    const oldChild = taskText(t.number);
    const childNew = newN + suffix;
    t.parentNumber = newN;
    remapTaskNumber(tasks, oldChild, childNew);
    t.number = childNew;
  }
}

/** Apply explicit sibling order and renumber 1..n at each level (updates descendants). */
function applySiblingOrder(tasks, parentNumber, orderedIds) {
  const pNum = taskText(parentNumber);
  const prefix = pNum ? `${pNum}.` : "";
  orderedIds.forEach((id, index) => {
    const t = tasks.find((x) => x.id === id);
    if (!t) return;
    const newNum = `${prefix}${index + 1}`;
    const oldNum = taskText(t.number);
    if (oldNum !== newNum) {
      remapTaskNumber(tasks, oldNum, newNum);
      t.number = newNum;
      t.parentNumber = pNum;
    }
  });
}

function orderedMinutesForTable(minutes) {
  const sorted = [...(minutes || [])].sort((a, b) => compareItemNumbers(a.number, b.number));
  let prevPrincipal = "";
  return sorted.map((minute) => {
    const principal = minute.principalTopic || "General";
    const showPrincipal = principal !== prevPrincipal;
    if (showPrincipal) prevPrincipal = principal;
    return { minute, principal, showPrincipal };
  });
}

function minuteSortValue(m, key, project) {
  switch (key) {
    case "number": return m.number || "";
    case "principal":
    case "principalTopic": return m.principalTopic || "";
    case "subTopic": return m.subTopic || "";
    case "owner": return m.owner || "";
    case "target": return parseDateMs(m.targetUpdated || m.targetOriginal) ?? (m.targetUpdated || m.targetOriginal || "");
    case "status": return String(m.status || "open").toLowerCase();
    case "notes": return (m.comments || []).length;
    case "tasks": return (project?.tasks || []).filter((t) => t.linkedMinuteId === m.id).length;
    default: return m.number || "";
  }
}

function orderedMinutesHierarchical(minutes, sortState, project) {
  const byPrincipal = new Map();
  for (const m of minutes || []) {
    const p = m.principalTopic || "General";
    if (!byPrincipal.has(p)) byPrincipal.set(p, []);
    byPrincipal.get(p).push(m);
  }
  const sortKey = sortState?.key || "number";
  const dir = sortState?.dir === "desc" ? -1 : 1;
  let principals = [...byPrincipal.keys()];
  if (sortKey === "principal" || sortKey === "principalTopic") {
    principals.sort((a, b) => compareSortValues(a, b, dir));
  } else {
    principals.sort((a, b) => {
      const aRows = sortItems(byPrincipal.get(a), sortState, (m, k) => minuteSortValue(m, k, project));
      const bRows = sortItems(byPrincipal.get(b), sortState, (m, k) => minuteSortValue(m, k, project));
      return compareSortValues(
        minuteSortValue(aRows[0], sortKey, project),
        minuteSortValue(bRows[0], sortKey, project),
        dir,
      );
    });
  }
  for (const p of principals) {
    byPrincipal.set(p, sortItems(byPrincipal.get(p), sortState, (m, k) => minuteSortValue(m, k, project)));
  }
  const result = [];
  let prev = "";
  for (const p of principals) {
    for (const minute of byPrincipal.get(p)) {
      const showPrincipal = p !== prev;
      if (showPrincipal) prev = p;
      result.push({ minute, principal: p, showPrincipal });
    }
  }
  return result;
}

function zebraRow(idx) {
  return idx % 2 === 0 ? "bg-white" : "bg-gray-50";
}

function minuteStatusMeta(value) {
  return MINUTE_STATUSES.find((s) => s.value === value) || MINUTE_STATUSES[0];
}

function progressTable(el, headers, rows) {
  return el("div", { class: "overflow-x-auto border border-gray-200 rounded-xl bg-white" }, [
    el("table", { class: "min-w-full text-left text-xs text-gray-800" }, [
      el("thead", { class: "bg-dae text-white uppercase" }, [
        el("tr", {}, headers.map((h) => el("th", { class: "px-2 py-2 font-medium whitespace-nowrap text-white text-[10px] tracking-wide", text: h }))),
      ]),
      el("tbody", {}, rows),
    ]),
  ]);
}

function sortableTh(el, label, colKey, sortState, persist, rerender, className = "px-2 py-2 font-medium whitespace-nowrap text-white text-[10px] tracking-wide cursor-pointer select-none hover:bg-white/15") {
  const active = sortState?.key === colKey;
  const arrow = active ? (sortState.dir === "asc" ? " ▲" : " ▼") : "";
  return el("th", {
    class: className,
    text: `${label}${arrow}`,
    onclick: () => {
      toggleSortState(sortState, colKey);
      persist?.();
      rerender();
    },
  });
}

function progressSortableTable(el, columns, rows, sortState, persist, rerender) {
  const headerCells = columns.map((col) => {
    if (!col.key || col.sortable === false) {
      return el("th", { class: "px-2 py-2 font-medium whitespace-nowrap text-white text-[10px] tracking-wide", text: col.label });
    }
    return sortableTh(el, col.label, col.key, sortState, persist, rerender);
  });
  return el("div", { class: "overflow-x-auto border border-gray-200 rounded-xl bg-white" }, [
    el("table", { class: "min-w-full text-left text-xs text-gray-800" }, [
      el("thead", { class: "bg-dae text-white uppercase" }, [el("tr", {}, headerCells)]),
      el("tbody", {}, rows),
    ]),
  ]);
}

const ICON_GRIP = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-4 h-4"><path d="M7 4a1 1 0 1 1-2 0 1 1 0 0 1 2 0ZM7 10a1 1 0 1 1-2 0 1 1 0 0 1 2 0ZM7 16a1 1 0 1 1-2 0 1 1 0 0 1 2 0ZM13 4a1 1 0 1 1-2 0 1 1 0 0 1 2 0ZM13 10a1 1 0 1 1-2 0 1 1 0 0 1 2 0ZM13 16a1 1 0 1 1-2 0 1 1 0 0 1 2 0Z"/></svg>';
const ICON_PLUS = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-4 h-4"><path d="M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z"/></svg>';
const ICON_TRASH = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-4 h-4"><path fill-rule="evenodd" d="M8.75 1.002a2.75 2.75 0 0 0-2.651 2.062l-.547 2.188A49.647 49.647 0 0 0 6 5.25c-.695.003-1.262.53-1.262 1.225v.11a48.568 48.568 0 0 0 0 7.19v.11c0 .695.567 1.222 1.262 1.225h10.98c.695-.003 1.262-.53 1.262-1.225v-.11a48.569 48.569 0 0 0 0-7.19v-.11c0-.695-.567-1.222-1.262-1.225a49.65 49.65 0 0 0-3.402-.998l-.547-2.187A2.75 2.75 0 0 0 11.25 1h-2.5ZM9.97 4.25l.547 2.188A49.647 49.647 0 0 0 10 6.5a48.568 48.568 0 0 0-7.19 0l.547-2.188A1.75 1.75 0 0 1 9.97 4.25ZM6 9.75a.75.75 0 0 1 .75-.75h6.5a.75.75 0 0 1 0 1.5h-6.5A.75.75 0 0 1 6 9.75Zm-.75 2.75a.75.75 0 0 1 .75-.75h6.5a.75.75 0 0 1 0 1.5h-6.5a.75.75 0 0 1-.75-.75Z" clip-rule="evenodd"/></svg>';
const ICON_LINK = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-4 h-4"><path d="M12.232 4.232a2.5 2.5 0 0 1 3.536 3.536l-1.225 1.224a.75.75 0 0 0 1.061 1.06l1.224-1.224a4 4 0 0 0-5.656-5.656l-3 3a4 4 0 0 0 .225 5.865.75.75 0 1 0 1.077-1.045 2.5 2.5 0 0 1-.142-3.667l3-3Z"/><path d="M11.603 7.963a.75.75 0 0 0-.977.073l-.128.154a.75.75 0 0 0 .073.977l1.224 1.224a2.5 2.5 0 0 1-.142 3.667l-3 3a2.5 2.5 0 0 1-3.536-3.536l1.225-1.224a.75.75 0 0 0-1.061-1.06l-1.224 1.224a4 4 0 1 0 5.656 5.656l3-3a4 4 0 0 0-.225-5.865.75.75 0 0 0-1.077 1.045Z"/></svg>';
const ICON_COMMENT = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-4 h-4"><path fill-rule="evenodd" d="M10 2c-2.236 0-4.43.18-6.57.524C1.962 2.56 1 3.426 1 4.81V13.5a2.5 2.5 0 0 0 2.5 2.5h2.387a.75.75 0 0 1 .53.22l2.074 2.073a.75.75 0 0 0 1.06 0l2.073-2.073a.75.75 0 0 1 .53-.22H16.5A2.5 2.5 0 0 0 19 13.5V4.81c0-1.383-.962-2.25-2.43-2.286A41.03 41.03 0 0 0 10 2Zm0 4.75a.75.75 0 0 1 .75.75v3.5a.75.75 0 0 1-1.5 0v-3.5A.75.75 0 0 1 10 7.5Zm0 7a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" clip-rule="evenodd"/></svg>';

function iconButton(el, { title, svg, danger = false, onClick }) {
  const btn = el("button", {
    type: "button",
    title,
    class: danger
      ? "inline-flex items-center justify-center w-7 h-7 rounded border border-gray-200 text-gray-400 hover:text-red-600 hover:border-red-200 hover:bg-red-50"
      : "inline-flex items-center justify-center w-7 h-7 rounded border border-gray-200 text-dae hover:bg-red-50",
    onclick: onClick,
  });
  btn.innerHTML = svg;
  return btn;
}

function actionLink(el, label, onclick, { danger = false, icon = null, title = null } = {}) {
  if (icon) return iconButton(el, { title: title || label, svg: icon, danger, onClick: onclick });
  return el("button", {
    type: "button",
    class: `text-xs font-medium ${danger ? "text-red-600 hover:text-red-800" : "text-dae hover:underline"}`,
    text: label,
    onclick,
  });
}

function confirmDelete(message, onConfirm) {
  if (!window.confirm(message)) return;
  onConfirm();
}

function rerenderAfterStructuralChange(ctx) {
  const scrollY = window.scrollY;
  const projectId =
    document.activeElement?.closest?.("[data-project-id]")?.getAttribute?.("data-project-id") || null;
  const fn = ctx.forceRerender || ctx.rerender;
  if (typeof fn === "function") fn();
  requestAnimationFrame(() => {
    window.scrollTo({ top: scrollY, left: 0, behavior: "instant" });
    if (projectId) {
      document.querySelector(`[data-project-id="${projectId}"]`)?.scrollIntoView?.({ block: "nearest" });
    }
  });
}

function sectionBar(ctx, title, actions = []) {
  const { el, sectionHeading } = ctx;
  return el("div", { class: "flex flex-wrap items-center justify-between gap-2 mb-2" }, [
    sectionHeading(title),
    actions.length ? el("div", { class: "flex flex-wrap items-center gap-1" }, actions) : null,
  ]);
}

function textCell(ctx, value, onCommit, { multiline = false, className = "px-2 py-1 text-gray-800" } = {}) {
  const { clickEditCell } = ctx;
  if (!clickEditCell) return el("td", { class: className, text: value || "—" });
  return clickEditCell({ value, multiline, className, onCommit: (next) => onCommit(next) });
}

function survProgressBar(el, pct) {
  const p = Math.max(0, Math.min(100, Math.round(Number(pct) || 0)));
  const color = p >= 100 ? "bg-green-500" : p > 0 ? "bg-dae" : "bg-gray-300";
  return el("div", { class: "w-full min-w-[4rem] bg-gray-100 rounded-full h-2 overflow-hidden", title: `${p}%` }, [
    el("div", { class: `${color} h-2 rounded-full`, style: { width: `${p}%` } }),
  ]);
}

const SURV_STATUS_COLORS = {
  "Not Started": "bg-gray-100 text-gray-700",
  Scheduled: "bg-blue-100 text-blue-700",
  "In Progress": "bg-amber-100 text-amber-800",
  Completed: "bg-green-100 text-green-700",
  Cancelled: "bg-gray-200 text-gray-500",
};

function survStatusPill(el, status) {
  const s = String(status || "—");
  const cls = SURV_STATUS_COLORS[s] || "bg-gray-100 text-gray-700";
  return el("span", { class: `inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cls}`, text: s });
}

function fmtSurvDate(value) {
  if (!value) return "—";
  try {
    return new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "short", year: "2-digit", timeZone: "UTC" }).format(new Date(value));
  } catch {
    return String(value);
  }
}

/** Operator-level surveillances with per-aircraft detail tables. */
export function renderOperatorSurveillances(ctx, survState) {
  const { el, sectionHeading, progressBadge, cleanText } = ctx;
  const { loading, campaigns = [] } = survState || {};
  if (loading) {
    return el("section", { class: "mb-6" }, [
      sectionHeading("I · Surveillances"),
      el("p", { class: "text-xs text-gray-500", text: "Loading surveillance data…" }),
    ]);
  }
  const open = campaigns.filter((c) => !OPEN_SURV_STATUSES.has(String(c.summary?.status || "").toLowerCase()));
  if (!open.length) return null;

  const blocks = open.map((camp) => {
    const s = camp.summary || {};
    const aircraft = camp.aircraft || [];
    const avgR = aircraft.length
      ? Math.round(aircraft.reduce((x, a) => x + Number(a.recordsPct || 0), 0) / aircraft.length)
      : Number(s.avgRecordsPct || 0);
    const avgP = aircraft.length
      ? Math.round(aircraft.reduce((x, a) => x + Number(a.physicalPct || 0), 0) / aircraft.length)
      : Number(s.avgPhysicalPct || 0);
    const rows = [...aircraft]
      .sort((a, b) => cleanText(a.msn).localeCompare(cleanText(b.msn), undefined, { numeric: true }))
      .map((a, idx) =>
      el("tr", { class: `border-t border-gray-100 hover:bg-gray-50 ${zebraRow(idx)}` }, [
        el("td", { class: "px-3 py-2 font-medium text-gray-900 whitespace-nowrap", text: cleanText(a.msn) || cleanText(a.registration) || "—" }),
        el("td", { class: "px-3 py-2 text-xs text-gray-600 max-w-[140px]", text: `${cleanText(a.inspectorRecordsName) || "—"} / ${cleanText(a.inspectorPhysicalName) || "—"}` }),
        el("td", { class: "px-3 py-2 w-28" }, [survProgressBar(el, a.recordsPct)]),
        el("td", { class: "px-3 py-2 w-28" }, [survProgressBar(el, a.physicalPct)]),
        el("td", { class: "px-3 py-2 whitespace-nowrap" }, [survStatusPill(el, a.overallStatus)]),
        el("td", { class: "px-3 py-2 text-gray-700 whitespace-nowrap text-xs", text: fmtSurvDate(a.actualInspectionDate || a.expectedInspectionDate) }),
        el("td", { class: "px-3 py-2 text-xs text-gray-600 whitespace-nowrap", text: `${cleanText(a.poRecords) || "—"} / ${cleanText(a.poPhysical) || "—"}` }),
      ])
    );
    return el("div", { class: "mb-4" }, [
      el("div", { class: "flex flex-wrap items-center justify-between gap-2 mb-2" }, [
        el("h3", { class: "text-xs font-bold text-gray-700 uppercase tracking-wide", text: `${s.quarter || camp.surveillance?.quarter}-${s.year || camp.surveillance?.year}` }),
        el("div", { class: "flex flex-wrap items-center gap-3 text-[11px] text-gray-600" }, [
          el("span", { text: `${aircraft.length || s.aircraftCount || 0} aircraft` }),
          el("span", { text: `R ${avgR}% · P ${avgP}%` }),
          survStatusPill(el, s.status || "Open"),
        ]),
      ]),
      aircraft.length
        ? el("div", { class: "bg-white rounded-lg border border-gray-200 overflow-hidden" }, [
            el("table", { class: "w-full text-sm text-gray-800" }, [
              el("thead", { class: "bg-gray-50 text-gray-600" }, [
                el("tr", {}, ["MSN", "Inspectors (R / P)", "Records", "Physical", "Overall", "Exp. date", "PO (R / P)"].map((h) =>
                  el("th", { class: "text-left font-medium px-3 py-1.5 text-xs whitespace-nowrap", text: h })
                )),
              ]),
              el("tbody", {}, rows),
            ]),
          ])
        : el("p", { class: "text-xs text-gray-400", text: "No aircraft attached to this campaign." }),
    ]);
  });

  return el("section", { class: "mb-6" }, [
    sectionHeading("I · Surveillances", { subtitle: "All open campaigns for this operator" }),
    ...blocks,
  ]);
}

function renderProjectHeader(ctx, project) {
  const { el, persist, rerender, progressBadge, clickEditCell } = ctx;
  const typeLabel = PROJECT_TYPES.find((t) => t.value === project.type)?.label || project.type;
  const summaryRow = clickEditCell
    ? clickEditCell({ value: project.summary || "", multiline: true, className: "px-2 py-2", onCommit: (v) => { project.summary = v; persist(); } })
    : el("td", { class: "px-2 py-2 text-gray-700", text: project.summary || "—" });
  if (summaryRow.colSpan !== undefined || summaryRow.tagName === "TD") summaryRow.colSpan = 3;
  const metaRows = [
    el("tr", { class: "border-t border-gray-100 bg-white" }, [
      el("td", { class: "px-2 py-2 font-medium text-gray-500 w-28", text: "Title" }),
      clickEditCell
        ? clickEditCell({ value: project.title || "", className: "px-2 py-2 font-medium text-gray-900", onCommit: (v) => { project.title = v || "Untitled project"; persist(); } })
        : el("td", { class: "px-2 py-2", text: project.title }),
      el("td", { class: "px-2 py-2 font-medium text-gray-500 w-20", text: "Type" }),
      clickEditCell
        ? clickEditCell({
            value: project.type || "general",
            className: "px-2 py-2",
            selectOptions: PROJECT_TYPES.map((t) => ({ value: t.value, label: t.label })),
            renderDisplay: (v) => progressBadge(PROJECT_TYPES.find((t) => t.value === v)?.label || v || "—", "gray"),
            onCommit: (v) => { project.type = v; persist(); },
          })
        : el("td", { class: "px-2 py-2" }, [progressBadge(typeLabel, "gray")]),
    ]),
    el("tr", { class: "border-t border-gray-100 bg-gray-50" }, [
      el("td", { class: "px-2 py-2 font-medium text-gray-500 align-top", text: "Summary" }),
      summaryRow,
    ]),
  ];
  const criticalRows = (project.criticalItems || []).map((item, idx) =>
    el("tr", { class: `border-t border-gray-100 ${zebraRow(idx)}` }, [
      textCell(ctx, item, (v) => { project.criticalItems[idx] = v; persist(); }, { className: "px-2 py-1" }),
      el("td", { class: "px-2 py-1 text-right w-16" }, [
        actionLink(el, "Delete", () => confirmDelete("Remove this critical item?", () => { project.criticalItems.splice(idx, 1); persist(); rerender(); }), { danger: true, icon: ICON_TRASH, title: "Delete" }),
      ]),
    ])
  );
  return el("section", { class: "mb-6" }, [
    sectionBar(ctx, "J · Project summary", [
      actionLink(el, "Add critical item", () => { project.criticalItems.push(""); persist(); rerender(); }, { icon: ICON_PLUS, title: "Add critical item" }),
    ]),
    progressTable(el, ["Field", "", "Field", ""], metaRows),
    criticalRows.length
      ? el("div", { class: "mt-3" }, [
          el("p", { class: "text-xs font-medium text-gray-500 uppercase mb-1", text: "Critical items" }),
          progressTable(el, ["Item", ""], criticalRows),
        ])
      : null,
  ]);
}

function contactSortKey(c) {
  return `${String(c?.lastName || "").trim()} ${String(c?.firstName || "").trim()}`.trim().toLowerCase();
}

function renderContactsSection(ctx, project, projectPrefs, persist) {
  const { el, persist: ctxPersist, rerender } = ctx;
  const save = persist || ctxPersist;
  const fields = [["firstName", "First"], ["lastName", "Last"], ["email", "Email"], ["phone", "Phone"], ["company", "Company"], ["position", "Role"]];
  const sortState = projectPrefs?.contactsSort || { key: "lastName", dir: "asc" };
  const contactSortValue = (c, key) => {
    if (key === "lastName") return contactSortKey(c);
    return c[key] || "";
  };
  const sortedContacts = sortItems(project.contacts || [], sortState, contactSortValue);
  const rows = sortedContacts.map((c, idx) =>
    el("tr", { class: `border-t border-gray-100 ${zebraRow(idx)}` }, [
      ...fields.map(([key]) => textCell(ctx, c[key] || "", (v) => { c[key] = v; persist(); }, { className: "px-2 py-1 whitespace-nowrap" })),
      el("td", { class: "px-2 py-1 text-right whitespace-nowrap" }, [
        actionLink(el, "Delete", () => confirmDelete("Delete this contact?", () => {
          const i = project.contacts.indexOf(c);
          if (i >= 0) project.contacts.splice(i, 1);
          persist(); rerender();
        }), { danger: true, icon: ICON_TRASH, title: "Delete contact" }),
      ]),
    ])
  );
  return el("section", { class: "mb-6" }, [
    sectionBar(ctx, "K · Contacts", [
      actionLink(el, "Add contact", () => {
        project.contacts.push({ id: newId("ct"), firstName: "", lastName: "", email: "", phone: "", company: "", position: "" });
        persist(); rerender();
      }, { icon: ICON_PLUS, title: "Add contact" }),
    ]),
    progressSortableTable(el, [
      ...fields.map(([key, label]) => ({ label, key })),
      { label: "", sortable: false },
    ], rows.length ? rows : [el("tr", {}, [el("td", { class: "px-3 py-4 text-gray-400", colspan: String(fields.length + 1), text: "No contacts yet." })])], sortState, save, rerender),
  ]);
}

function ensureMinuteTracker(minute) {
  if (!minute._tracker || typeof minute._tracker !== "object") minute._tracker = {};
  const t = minute._tracker;
  if (!Array.isArray(t.history)) t.history = [];
  // Drop empty XLS placeholders left from an earlier UI experiment.
  t.history = t.history.filter(
    (h) => String(h?.text || "").trim() || String(h?.date || "").trim()
  );
  if (t.nextAction == null) {
    const legacy = Array.isArray(t.nextActions) ? t.nextActions : [];
    const first = legacy[0];
    t.nextAction = typeof first === "string" ? first : first?.text || "";
    t.nextActionBy = first?.owner || minute.owner || t.nextActionBy || "";
    t.targetDate = first?.target || minute.targetUpdated || t.targetDate || "";
  }
  if (t.nextActionBy == null) t.nextActionBy = minute.owner || "";
  if (t.targetDate == null) t.targetDate = minute.targetUpdated || "";
  if (!t.status) t.status = "PENDING";
  if (!t.priority) t.priority = "NORMAL";
  minute.targetUpdated = t.targetDate;
  return t;
}

function formatHistoryForEdit(history) {
  return sortHistoryRecentFirst(history).map((h) => {
    const d = String(h.date || "").trim();
    const text = String(h.text || "").replace(/\r?\n/g, "\\n");
    return d ? `${d} | ${text}` : text;
  }).join("\n");
}

function parseHistoryFromEdit(raw) {
  return String(raw || "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      // Legacy: date | XLS|MTG | text
      const tagged = line.match(/^([^|]*)\|\s*(MTG|XLS|excel|meeting)\s*\|\s*(.+)$/i);
      if (tagged) {
        return {
          date: tagged[1].trim(),
          source: /^mtg|meeting$/i.test(tagged[2]) ? "meeting" : "excel",
          text: tagged[3].trim().replace(/\\n/g, "\n"),
        };
      }
      // Current: date | text  (or bare text)
      const plain = line.match(/^([^|]+)\|\s*(.+)$/);
      if (plain && parseDateMs(plain[1].trim()) != null) {
        return { date: plain[1].trim(), source: "meeting", text: plain[2].trim().replace(/\\n/g, "\n") };
      }
      return { date: "", source: "meeting", text: line.replace(/\\n/g, "\n") };
    });
}

function ensureDatedHistory(row, historyKey, legacyKey) {
  if (!row || typeof row !== "object") return { history: [] };
  if (!Array.isArray(row[historyKey])) {
    const legacy = String(row[legacyKey] || "").trim();
    if (!legacy) {
      row[historyKey] = [];
    } else {
      const parsed = parseHistoryFromEdit(legacy);
      const looksDated = parsed.some((h) => String(h?.date || "").trim());
      row[historyKey] = looksDated ? parsed : [{ date: "", source: "meeting", text: legacy }];
    }
  }
  return { history: row[historyKey] };
}

/** Seed dated comments from a legacy free-text notes field (engines / similar). */
export function engineReasonTracker(row) {
  return ensureDatedHistory(row, "reasonHistory", "reason");
}

export function syncReasonFromHistory(row) {
  if (!row || typeof row !== "object") return;
  row.reason = formatHistoryForEdit(row.reasonHistory);
}

/** RTS Parts / comments: same dated chronology as minutes and engines. */
export function rtsPartsTracker(row) {
  return ensureDatedHistory(row, "partsHistory", "parts");
}

export function syncPartsFromHistory(row) {
  if (!row || typeof row !== "object") return;
  row.parts = formatHistoryForEdit(row.partsHistory);
}

function sortHistoryRecentFirst(history) {
  return [...(history || [])]
    .map((item, idx) => ({ item, idx, ms: parseDateMs(item?.date) }))
    .sort((a, b) => {
      if (a.ms != null && b.ms != null && a.ms !== b.ms) return b.ms - a.ms;
      if (a.ms != null && b.ms == null) return -1;
      if (a.ms == null && b.ms != null) return 1;
      return b.idx - a.idx;
    })
    .map(({ item }) => item);
}

function historyEntryLine(el, item, { compact = false } = {}) {
  const dateLabel = item.date ? formatDdMmmYy(item.date) || String(item.date).trim() : "";
  const today = dateLabel && isChronologyToday(item.date);
  return el("div", {
    class: today
      ? "flex gap-1.5 text-xs leading-snug text-blue-700 font-semibold"
      : "flex gap-1.5 text-xs leading-snug text-gray-800",
  }, [
    dateLabel
      ? el("span", {
          class: today
            ? "font-mono text-[10px] text-blue-700 font-bold shrink-0 tabular-nums"
            : "font-mono text-[10px] text-gray-600 shrink-0 tabular-nums",
          text: dateLabel,
        })
      : null,
    el("span", {
      class: compact ? "flex-1 min-w-0 truncate" : "flex-1 min-w-0 whitespace-pre-wrap break-words",
      text: item.text || "",
    }),
  ]);
}

function sizeTextarea(ta) {
  ta.style.height = "auto";
  ta.style.height = `${Math.max(56, ta.scrollHeight)}px`;
}

function chronologyDeleteBtn(el, onDelete) {
  return el("button", {
    type: "button",
    class: "shrink-0 text-gray-400 hover:text-red-700 px-0.5 text-sm leading-none self-start",
    text: "×",
    title: "Delete this line",
    onclick: (e) => {
      e.preventDefault();
      e.stopPropagation();
      onDelete();
    },
  });
}

function chronologyEditorFields(el, { dateIso, text, placeholder = "" }) {
  const dateInp = el("input", {
    type: "date",
    class: "w-[8.6rem] shrink-0 rounded border border-dae/40 px-1 py-0.5 text-[11px]",
    value: dateIso || "",
    title: "Date",
    "data-op-block-rerender": "1",
    onclick: (e) => e.stopPropagation(),
    onmousedown: (e) => e.stopPropagation(),
  });
  const ta = el("textarea", {
    class: "flex-1 min-w-0 rounded border border-dae/40 px-1.5 py-1 text-xs text-gray-800 leading-snug resize-y",
    rows: String(Math.max(2, String(text || "").split("\n").length)),
    placeholder,
    "data-op-block-rerender": "1",
    onclick: (e) => e.stopPropagation(),
    onmousedown: (e) => e.stopPropagation(),
  });
  ta.value = text || "";
  return { dateInp, ta };
}

function bindChronologyEditor(wrap, { dateInp, ta, onSave, onCancel }) {
  let closed = false;
  const finish = (commit) => {
    if (closed) return;
    closed = true;
    if (!commit) {
      onCancel();
      return;
    }
    onSave(chronologyFromIso(dateInp.value), String(ta.value || "").trim());
  };
  ta.addEventListener("keydown", (ev) => {
    if (ev.key === "Enter" && !ev.shiftKey) {
      ev.preventDefault();
      finish(true);
    } else if (ev.key === "Escape") {
      ev.preventDefault();
      finish(false);
    }
  });
  dateInp.addEventListener("keydown", (ev) => {
    if (ev.key === "Escape") {
      ev.preventDefault();
      finish(false);
    } else if (ev.key === "Enter") {
      ev.preventDefault();
      ta.focus();
    }
  });
  ta.addEventListener("input", () => sizeTextarea(ta));
  wrap.addEventListener("focusout", (ev) => {
    const next = ev.relatedTarget;
    if (next && wrap.contains(next)) return;
    setTimeout(() => {
      if (closed) return;
      if (wrap.contains(document.activeElement)) return;
      finish(true);
    }, 180);
  });
  return {
    focus() {
      ta.focus();
      const end = ta.value.length;
      try { ta.setSelectionRange(end, end); } catch { /* ignore */ }
      sizeTextarea(ta);
    },
  };
}

const CHRONOLOGY_ADD = { _add: true };
const CHRONOLOGY_PLUS_SVG = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-3.5 h-3.5"><path d="M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z"/></svg>';

/** Chronology cell: notes display as text; plus adds; click a line to edit; datepicker; × always. */
export function chronologyCell(el, tr, persist, { className = "px-2 py-2 min-w-[22rem] w-[42%] align-top" } = {}) {
  if (!Array.isArray(tr.history)) tr.history = [];
  const tdClass = /\brelative\b/.test(className) ? `${className} pr-7` : `${className} relative pr-7`;
  const td = el("td", { class: tdClass });
  const list = el("div", { class: "space-y-1 pr-0.5" });
  let editing = null;
  let addGuard = "";
  let editorGen = 0;

  const visibleItems = () => sortHistoryRecentFirst(tr.history).filter(
    (h) => String(h?.text || "").trim() || String(h?.date || "").trim()
  );

  const closeEditor = () => {
    editorGen += 1;
    editing = null;
    delete td.dataset.opEditing;
  };

  const saveAndRefresh = () => {
    persist();
    if (td.isConnected) renderList();
  };

  const deleteItem = (item) => {
    tr.history = (tr.history || []).filter((h) => h !== item);
    if (editing === item) closeEditor();
    saveAndRefresh();
  };

  const startAdd = () => {
    editorGen += 1;
    editing = CHRONOLOGY_ADD;
    td.dataset.opEditing = "1";
    renderList();
  };

  const startEdit = (item) => {
    editorGen += 1;
    editing = item;
    td.dataset.opEditing = "1";
    renderList();
  };

  const renderEditor = ({ dateIso, text, placeholder, onSave, onCancel }) => {
    const gen = editorGen;
    const wrap = el("div", {
      class: "flex gap-1 items-start w-full min-w-0",
      "data-op-block-rerender": "1",
      onclick: (e) => e.stopPropagation(),
      onmousedown: (e) => e.stopPropagation(),
    });
    const { dateInp, ta } = chronologyEditorFields(el, { dateIso, text, placeholder });
    wrap.appendChild(dateInp);
    wrap.appendChild(ta);
    const bound = bindChronologyEditor(wrap, {
      dateInp,
      ta,
      onSave: (date, note) => {
        if (gen !== editorGen) return;
        onSave(date, note);
      },
      onCancel: () => {
        if (gen !== editorGen) return;
        onCancel();
      },
    });
    queueMicrotask(() => bound.focus());
    return wrap;
  };

  const renderList = () => {
    list.replaceChildren();
    if (editing === CHRONOLOGY_ADD) {
      const addRow = el("div", { class: "flex gap-1 items-start" });
      addRow.appendChild(renderEditor({
        dateIso: chronologyToIso(todayDdMmmYy()),
        text: "",
        placeholder: "New note — Enter to save",
        onSave: (date, text) => {
          if (!text) {
            closeEditor();
            if (td.isConnected) renderList();
            return;
          }
          const key = `${date}\n${text}`;
          const last = tr.history[tr.history.length - 1];
          if (addGuard === key || (last && String(last.date || "") === date && String(last.text || "").trim() === text)) {
            closeEditor();
            if (td.isConnected) renderList();
            return;
          }
          tr.history.push({ date: date || todayDdMmmYy(), source: "meeting", text });
          addGuard = key;
          closeEditor();
          saveAndRefresh();
        },
        onCancel: () => {
          closeEditor();
          if (td.isConnected) renderList();
        },
      }));
      addRow.appendChild(chronologyDeleteBtn(el, () => {
        closeEditor();
        if (td.isConnected) renderList();
      }));
      list.appendChild(addRow);
    }

    const items = visibleItems();
    if (!items.length && editing !== CHRONOLOGY_ADD) {
      list.appendChild(el("span", { class: "text-gray-400 text-xs", text: "—" }));
    }
    for (const item of items) {
      const row = el("div", { class: "flex gap-1 items-start group" });
      if (editing === item) {
        const originalDate = item.date ? formatDdMmmYy(item.date) || String(item.date).trim() : "";
        const originalText = String(item.text || "");
        row.appendChild(renderEditor({
          dateIso: chronologyToIso(item.date),
          text: originalText,
          onSave: (date, text) => {
            if (!text && !date) {
              deleteItem(item);
              return;
            }
            if (date === originalDate && text === originalText.trim()) {
              closeEditor();
              if (td.isConnected) renderList();
              return;
            }
            item.date = date;
            item.text = text;
            if (!item.text && !item.date) {
              deleteItem(item);
              return;
            }
            closeEditor();
            saveAndRefresh();
          },
          onCancel: () => {
            closeEditor();
            if (td.isConnected) renderList();
          },
        }));
      } else {
        const display = el("div", {
          class: "flex-1 min-w-0 rounded px-0.5 -mx-0.5 cursor-text hover:bg-gray-50",
          title: "Click to edit",
          onclick: (e) => {
            e.stopPropagation();
            startEdit(item);
          },
          onmousedown: (e) => e.stopPropagation(),
        });
        display.appendChild(historyEntryLine(el, item, { compact: false }));
        row.appendChild(display);
      }
      row.appendChild(chronologyDeleteBtn(el, () => deleteItem(item)));
      list.appendChild(row);
    }
  };

  const plus = el("button", {
    type: "button",
    class: "absolute top-1 right-1 z-10 inline-flex items-center justify-center w-5 h-5 rounded border border-gray-200 bg-white text-dae hover:bg-red-50",
    title: "Add line",
    onclick: (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (editing === CHRONOLOGY_ADD) return;
      startAdd();
    },
    onmousedown: (e) => e.stopPropagation(),
  });
  plus.innerHTML = CHRONOLOGY_PLUS_SVG;

  renderList();
  td.appendChild(plus);
  td.appendChild(list);
  return td;
}

export function disposeHistoryPopover() {
  hideHistoryPopover();
}

let historyPopoverEl = null;
let historyPopoverHideTimer = null;

function hideHistoryPopover() {
  clearTimeout(historyPopoverHideTimer);
  historyPopoverHideTimer = null;
  if (historyPopoverEl) {
    historyPopoverEl.remove();
    historyPopoverEl = null;
  }
}

function minuteHasTrackerDetail(minute) {
  const t = minute?._tracker;
  return Boolean(t?.history?.length || t?.nextAction || t?.nextActions?.length || t?.status || t?.priority);
}

function projectUsesRichMinutes(project) {
  return Boolean(project?.azulTracker?.sections?.length)
    || (project.minutes || []).some(minuteHasTrackerDetail);
}

function trackerStatusVariant(status) {
  const s = String(status || "").toUpperCase();
  if (s === "IN PROGRESS") return "green";
  if (s === "PENDING") return "amber";
  if (s === "CLOSED") return "gray";
  return "blue";
}

function trackerPriorityVariant(priority) {
  return String(priority || "").toUpperCase() === "CRITICAL" ? "red" : "gray";
}

function subtopicTrackerClosed(minute) {
  return String(ensureMinuteTracker(minute).status || "").toUpperCase() === "CLOSED";
}

function renderMinutesRichSection(ctx, project, uiPrefs, projectPrefs, persist) {
  const { el, persist: ctxPersist, rerender, forceRerender, progressBadge, clickEditCell, clickEditDateCell, cleanText } = ctx;
  const save = persist || ctxPersist;
  const refreshStructural = () => rerenderAfterStructuralChange(ctx);
  if (repairMinuteNumbering(project.minutes)) {
    save?.();
  }
  const filter = uiPrefs?.minutesFilter || "open";
  const sortState = projectPrefs?.minutesSort || { key: "number", dir: "asc" };
  const visibleMinutes = (project.minutes || []).filter((m) => {
    if (filter === "all") return true;
    if (filter === "closed") return subtopicTrackerClosed(m);
    return !subtopicTrackerClosed(m);
  });

  // Group in original numbered order (1…N) — same order used in the .eml export.
  const sorted = [...visibleMinutes].sort((a, b) => compareItemNumbers(a.number, b.number));
  const groups = [];
  const groupIndex = new Map();
  for (const minute of sorted) {
    const key = minute.principalTopic || "General";
    if (!groupIndex.has(key)) {
      groupIndex.set(key, groups.length);
      const sec =
        String(minute.sectionNumber || "").trim() ||
        String(minute.number || "").trim().match(/^(\d+)/)?.[1] ||
        "";
      groups.push({
        principal: key,
        sectionNumber: sec,
        minutes: [],
      });
    }
    groups[groupIndex.get(key)].minutes.push(minute);
  }

  const richMinuteSortValue = (minute, key) => {
    const tr = ensureMinuteTracker(minute);
    switch (key) {
      case "number": return minute.number || "";
      case "principal":
      case "principalTopic": return minute.principalTopic || "";
      case "subTopic": return minute.subTopic || "";
      case "history": return (tr.history || []).length;
      case "nextAction": return tr.nextAction || "";
      case "nextActionBy": return tr.nextActionBy || "";
      case "target": return parseDateMs(tr.targetDate) ?? (tr.targetDate || "");
      case "status": return tr.status || "";
      case "priority": return tr.priority || "";
      default: return minute.number || "";
    }
  };

  const dir = sortState?.dir === "desc" ? -1 : 1;
  const sortKey = sortState?.key || "number";
  const sortedGroups = [...groups];
  if (sortKey === "principal" || sortKey === "principalTopic") {
    sortedGroups.sort((a, b) => compareSortValues(a.principal, b.principal, dir));
  } else if (sortKey !== "number") {
    sortedGroups.sort((a, b) => {
      const aRows = sortItems(a.minutes, sortState, richMinuteSortValue);
      const bRows = sortItems(b.minutes, sortState, richMinuteSortValue);
      return compareSortValues(
        richMinuteSortValue(aRows[0], sortKey),
        richMinuteSortValue(bRows[0], sortKey),
        dir,
      );
    });
  }
  for (const g of sortedGroups) {
    g.minutes = sortItems(g.minutes, sortState, richMinuteSortValue);
  }

  const addSubtopic = (principalTopic, sectionNumber = "") => {
    let sec = String(sectionNumber || "").trim() || sectionNumberForTopic(project.minutes, principalTopic);
    if (!sec) sec = nextTopLevelSection(project.minutes);

    // Seed row numbered exactly "8" becomes 8.1 when the first child is added.
    const parentLevel = (project.minutes || []).find(
      (m) =>
        (m.principalTopic || "General") === principalTopic && String(m.number || "").trim() === sec
    );
    if (parentLevel) {
      parentLevel.number = `${sec}.1`;
      parentLevel.sectionNumber = sec;
    }

    const num = nextMinuteChildNumber(project.minutes, principalTopic, sec);
    project.minutes.push({
      id: newId("min"),
      number: num,
      sectionNumber: sec,
      principalTopic,
      subTopic: "",
      owner: "",
      targetOriginal: "",
      targetUpdated: "",
      status: "open",
      comments: [],
      _tracker: {
        status: "PENDING",
        priority: "NORMAL",
        history: [],
        nextAction: "",
        nextActionBy: "",
        targetDate: "",
      },
    });
    persist();
    rerender();
  };

  const addTopic = () => {
    const n = nextTopLevelSection(project.minutes);
    addSubtopic(`New topic ${n}`, n);
  };

  const renameTopic = (oldTitle, sectionNumber) => {
    const next = window.prompt("Topic title", oldTitle);
    if (next == null) return;
    const cleaned = cleanText(next);
    if (!cleaned || cleaned === oldTitle) return;
    for (const m of project.minutes || []) {
      if ((m.principalTopic || "General") === oldTitle) {
        m.principalTopic = cleaned;
        if (sectionNumber) m.sectionNumber = String(sectionNumber);
      }
    }
    persist();
    refreshStructural();
  };

  const tableRows = [];
  const colCount = 9;

  for (const { principal, sectionNumber, minutes } of sortedGroups) {
    const sec =
      String(sectionNumber || "").trim() || sectionNumberForTopic(minutes, principal) || "";
    tableRows.push(
      el("tr", { class: "bg-slate-100 border-y border-slate-200" }, [
        el("td", {
          colspan: String(colCount),
          class: "px-3 py-1.5 text-xs font-semibold text-slate-700 tracking-wide",
        }, [
          el("div", { class: "flex items-center gap-2 flex-wrap" }, [
            sec ? el("span", { class: "font-mono text-slate-500", text: `${sec} ·` }) : null,
            el("button", {
              type: "button",
              class:
                "text-left font-semibold text-slate-700 hover:text-dae hover:underline cursor-pointer bg-transparent border-0 p-0",
              text: principal,
              title: "Click to rename this topic",
              onclick: () => renameTopic(principal, sec),
            }),
            el("span", {
              class: "text-[10px] font-normal text-slate-400",
              text: "click title to rename",
            }),
          ]),
        ]),
      ])
    );

    for (const [idx, minute] of minutes.entries()) {
      const tr = ensureMinuteTracker(minute);
      const commitTracker = (patch) => {
        Object.assign(tr, patch);
        if (patch.targetDate != null) minute.targetUpdated = patch.targetDate;
        if (String(patch.status || "").toUpperCase() === "CLOSED") minute.status = "closed";
        persist();
      };

      tableRows.push(
        el("tr", { class: `align-top border-t border-gray-100 ${zebraRow(idx)}` }, [
          textCell(ctx, minute.number || "", (v) => { minute.number = v; persist(); }, { className: "px-2 py-2 font-mono text-gray-600 w-14 whitespace-nowrap" }),
          textCell(ctx, minute.subTopic || "", (v) => { minute.subTopic = v; persist(); }, { className: "px-2 py-2 font-medium text-gray-900 w-[9rem] max-w-[9rem] min-w-[9rem] whitespace-pre-wrap break-words" }),
          chronologyCell(el, tr, persist),
          textCell(ctx, tr.nextAction || "", (v) => { tr.nextAction = v; persist(); }, { multiline: true, className: "px-2 py-2 min-w-[240px] max-w-xl whitespace-pre-wrap break-words" }),
          textCell(ctx, tr.nextActionBy || "", (v) => { tr.nextActionBy = v; persist(); }, { className: "px-2 py-2 min-w-[100px] whitespace-nowrap" }),
          clickEditDateCell
            ? clickEditDateCell({
                value: tr.targetDate || "",
                className: "px-2 py-2 whitespace-nowrap",
                onCommit: (v) => { commitTracker({ targetDate: v }); },
              })
            : textCell(ctx, tr.targetDate || "", (v) => commitTracker({ targetDate: v }), { className: "px-2 py-2 whitespace-nowrap" }),
          clickEditCell
            ? clickEditCell({
                value: tr.status || "PENDING",
                className: "px-2 py-2 whitespace-nowrap",
                selectOptions: TRACKER_STATUSES.map((s) => ({ value: s.value, label: s.label })),
                renderDisplay: (v) => {
                  const sm = TRACKER_STATUSES.find((s) => s.value === v) || TRACKER_STATUSES[1];
                  return progressBadge(sm.label, sm.variant);
                },
                onCommit: (v) => {
                  commitTracker({ status: v });
                  if (String(v || "").toUpperCase() === "CLOSED" && filter === "open") refreshStructural();
                },
              })
            : el("td", { class: "px-2 py-2" }, [progressBadge(tr.status, trackerStatusVariant(tr.status))]),
          clickEditCell
            ? clickEditCell({
                value: tr.priority || "NORMAL",
                className: "px-2 py-2 whitespace-nowrap",
                selectOptions: TRACKER_PRIORITIES.map((p) => ({ value: p.value, label: p.label })),
                renderDisplay: (v) => {
                  const pm = TRACKER_PRIORITIES.find((p) => p.value === v) || TRACKER_PRIORITIES[0];
                  return progressBadge(pm.label, pm.variant);
                },
                onCommit: (v) => { commitTracker({ priority: v }); },
              })
            : el("td", { class: "px-2 py-2" }, [
              progressBadge(tr.priority === "CRITICAL" ? "Critical" : "Normal", trackerPriorityVariant(tr.priority)),
            ]),
          el("td", { class: "px-2 py-2 text-right whitespace-nowrap align-top" }, [
            el("div", { class: "inline-flex items-center gap-1" }, [
              actionLink(el, "Add row", () => addSubtopic(principal, sec), { icon: ICON_PLUS, title: "Add subtopic under this topic" }),
              actionLink(el, "Delete", () => confirmDelete(`Delete subtopic ${minute.number || ""}?`, () => {
                project.minutes = project.minutes.filter((x) => x.id !== minute.id);
                persist(); refreshStructural();
              }), { danger: true, icon: ICON_TRASH, title: "Delete subtopic" }),
            ]),
          ]),
        ])
      );
    }
  }

  const filterSelect = el("select", {
    class: "text-xs border border-gray-200 rounded-lg px-2 py-1",
    value: filter,
    onchange: (ev) => {
      uiPrefs.minutesFilter = cleanText(ev.target.value) || "open";
      persist(); rerender();
    },
  }, MINUTE_STATUS_FILTERS.map((f) => el("option", { value: f.value, text: f.label, selected: f.value === filter })));

  return el("section", { class: "mb-6" }, [
    sectionBar(ctx, "L · Meeting minutes", [
      el("label", { class: "inline-flex items-center gap-1 text-xs text-gray-600" }, [
        el("span", { text: "Show" }),
        filterSelect,
      ]),
      actionLink(el, "Add topic", addTopic, { icon: ICON_PLUS, title: "Add main topic with first subtopic" }),
    ]),
    el("p", { class: "text-xs text-gray-500 mb-2", text: "Chronology: click a note to expand. Enter saves; Shift+Enter adds a new line. × deletes that line." }),
    el("div", { class: "overflow-x-auto border border-gray-200 rounded-xl bg-white" }, [
      el("table", { class: "min-w-full table-fixed text-left text-xs text-gray-800" }, [
        el("thead", { class: "bg-dae text-white uppercase" }, [
          el("tr", {}, [
            sortableTh(el, "Item", "number", sortState, save, rerender, "px-2 py-2 font-medium whitespace-nowrap text-white text-[10px] tracking-wide cursor-pointer select-none hover:bg-white/15 w-12"),
            sortableTh(el, "Topic", "subTopic", sortState, save, rerender, "px-2 py-2 font-medium text-white text-[10px] tracking-wide cursor-pointer select-none hover:bg-white/15 w-[9rem] max-w-[9rem] whitespace-normal"),
            sortableTh(el, "Chronology", "history", sortState, save, rerender, "px-2 py-2 font-medium whitespace-nowrap text-white text-[10px] tracking-wide cursor-pointer select-none hover:bg-white/15 w-[42%]"),
            sortableTh(el, "Next action", "nextAction", sortState, save, rerender),
            sortableTh(el, "Responsible", "nextActionBy", sortState, save, rerender),
            sortableTh(el, "Target", "target", sortState, save, rerender),
            sortableTh(el, "Status", "status", sortState, save, rerender),
            sortableTh(el, "Priority", "priority", sortState, save, rerender),
            el("th", { class: "px-2 py-2 font-medium whitespace-nowrap text-white text-[10px] tracking-wide", text: "" }),
          ]),
        ]),
        el("tbody", {}, tableRows.length
          ? tableRows
          : [el("tr", {}, [el("td", { class: "px-3 py-6 text-gray-400 text-center", colspan: String(colCount), text: filter === "open" ? "No open meeting minutes." : "No meeting minutes for this filter." })])]),
      ]),
    ]),
  ]);
}

function renderMinuteNotesBlock(ctx, minute, persist, rerender) {
  const { el } = ctx;
  const comments = sortCommentsNewestFirst(minute.comments);
  const latest = comments[0];
  const addNote = () => openCommentModal(el, {
    title: "Add note",
    onSubmit: (text) => {
      minute.comments.unshift({ id: newId("c"), text, at: new Date().toISOString(), by: "user" });
      persist(); rerender();
    },
  });
  if (!latest) {
    return el("div", { class: "pt-1" }, [
      el("div", { class: "text-[10px] font-bold uppercase text-gray-500 mb-1", text: "Notes" }),
      actionLink(el, "Add note", addNote, { icon: ICON_COMMENT, title: "Add note" }),
    ]);
  }
  return el("div", { class: "pt-1" }, [
    el("div", { class: "text-[10px] font-bold uppercase text-gray-500 mb-1", text: "Notes & comments" }),
    el("details", { open: false }, [
      el("summary", { class: "cursor-pointer text-gray-800 hover:text-dae list-none text-xs", text: truncateText(latest.text, 96) || "—" }),
      el("div", { class: "mt-1 space-y-1 border-t border-gray-200 pt-1" }, [
        ...comments.map((c) => el("div", { class: "text-[11px] text-gray-600 leading-snug", text: `${fmtWhen(c.at)} — ${c.text}` })),
        actionLink(el, "Add note", addNote, { icon: ICON_COMMENT, title: "Add note" }),
      ]),
    ]),
  ]);
}

function renderMinutesNotesCell(ctx, minute, persist, rerender) {
  const { el } = ctx;
  return el("td", { class: "px-2 py-1 align-top max-w-xs" }, [
    renderMinuteNotesBlock(ctx, minute, persist, rerender),
  ]);
}

function renderMinutesSection(ctx, project, uiPrefs, projectPrefs, persist) {
  if (projectUsesRichMinutes(project)) {
    return renderMinutesRichSection(ctx, project, uiPrefs, projectPrefs, persist);
  }
  const { el, persist: ctxPersist, rerender, progressBadge, clickEditCell, cleanText } = ctx;
  const save = persist || ctxPersist;
  const filter = uiPrefs?.minutesFilter || "open";
  const sortState = projectPrefs?.minutesSort || { key: "number", dir: "asc" };
  const visibleMinutes = (project.minutes || []).filter((m) => {
    const st = String(m.status || "open").toLowerCase();
    if (filter === "all") return true;
    if (filter === "closed") return st === "closed";
    return st !== "closed";
  });
  const ordered = orderedMinutesHierarchical(visibleMinutes, sortState, project);
  const addMinute = (principalTopic = "New topic") => {
    project.minutes.push({
      id: newId("min"),
      number: String((project.minutes.length || 0) + 1),
      principalTopic,
      subTopic: "",
      owner: "",
      targetOriginal: "",
      targetUpdated: "",
      status: "open",
      comments: [],
    });
    persist(); rerender();
  };
  const addSubTopic = (minute) => {
    project.minutes.push({
      id: newId("min"),
      number: String((project.minutes.length || 0) + 1),
      principalTopic: minute.principalTopic || "General",
      subTopic: "",
      owner: "",
      targetOriginal: "",
      targetUpdated: "",
      status: "open",
      comments: [],
    });
    persist(); rerender();
  };
  const rows = ordered.map(({ minute, principal, showPrincipal }, idx) => {
    const target = minute.targetUpdated || minute.targetOriginal || "";
    const linked = (project.tasks || []).filter((t) => t.linkedMinuteId === minute.id);
    const mm = minuteStatusMeta(String(minute.status || "open").toLowerCase());
    const statusCell = clickEditCell
      ? clickEditCell({
          value: minute.status || "open",
          className: "px-2 py-1",
          selectOptions: MINUTE_STATUSES.map((s) => ({ value: s.value, label: s.label })),
          renderDisplay: (v) => {
            const mm = minuteStatusMeta(String(v || "open").toLowerCase());
            return progressBadge(mm.label, mm.variant);
          },
          onCommit: (v) => {
            minute.status = v;
            persist();
            if (String(v || "").toLowerCase() === "closed" && filter === "open") rerenderAfterStructuralChange(ctx);
          },
        })
      : el("td", { class: "px-2 py-1" }, [progressBadge(mm.label, mm.variant)]);
    return el("tr", { class: `border-t border-gray-100 ${zebraRow(idx)} align-top` }, [
      el("td", { class: "px-2 py-1 font-mono text-gray-700 whitespace-nowrap", text: minute.number || "—" }),
      showPrincipal
        ? textCell(ctx, principal, (v) => { minute.principalTopic = v; persist(); }, { className: "px-2 py-1 font-medium text-gray-900 max-w-[140px]" })
        : el("td", { class: "px-2 py-1 text-gray-300", text: "↳" }),
      textCell(ctx, minute.subTopic || "", (v) => { minute.subTopic = v; persist(); }, { className: "px-2 py-1 text-gray-800 max-w-[200px]" }),
      textCell(ctx, minute.owner || "", (v) => { minute.owner = v; persist(); }, { className: "px-2 py-1 whitespace-nowrap" }),
      textCell(ctx, target, (v) => { minute.targetUpdated = v; persist(); }, { className: "px-2 py-1 whitespace-nowrap" }),
      statusCell,
      renderMinutesNotesCell(ctx, minute, persist, rerender),
      el("td", { class: "px-2 py-1 text-gray-500 whitespace-nowrap", text: linked.length ? linked.map((t) => t.number).join(", ") : "—" }),
      el("td", { class: "px-2 py-1 text-right whitespace-nowrap" }, [
        el("div", { class: "inline-flex items-center gap-1 justify-end" }, [
          actionLink(el, "Add sub-topic", () => addSubTopic(minute), { icon: ICON_PLUS, title: "Add sub-topic" }),
          actionLink(el, "Delete", () => confirmDelete("Delete this minute row?", () => {
            project.minutes = project.minutes.filter((x) => x.id !== minute.id);
            persist(); rerenderAfterStructuralChange(ctx);
          }), { danger: true, icon: ICON_TRASH, title: "Delete minute" }),
        ]),
      ]),
    ]);
  });
  const filterSelect = el("select", {
    class: "text-xs border border-gray-200 rounded-lg px-2 py-1",
    value: filter,
    onchange: (ev) => {
      uiPrefs.minutesFilter = cleanText(ev.target.value) || "open";
      persist(); rerender();
    },
  }, MINUTE_STATUS_FILTERS.map((f) => el("option", { value: f.value, text: f.label, selected: f.value === filter })));
  return el("section", { class: "mb-6" }, [
    sectionBar(ctx, "L · Meeting minutes", [
      el("label", { class: "inline-flex items-center gap-1 text-xs text-gray-600" }, [
        el("span", { text: "Show" }),
        filterSelect,
      ]),
      actionLink(el, "Add topic", () => addMinute("New topic"), { icon: ICON_PLUS, title: "Add topic" }),
      actionLink(el, "Add sub-topic", () => {
        const last = project.minutes[project.minutes.length - 1];
        addSubTopic(last || { principalTopic: "New topic" });
      }, { icon: ICON_PLUS, title: "Add sub-topic" }),
    ]),
    progressSortableTable(el, [
      { label: "#", key: "number" },
      { label: "Principal", key: "principal" },
      { label: "Sub-topic", key: "subTopic" },
      { label: "Owner", key: "owner" },
      { label: "Target", key: "target" },
      { label: "Status", key: "status" },
      { label: "Latest notes", key: "notes", sortable: false },
      { label: "Tasks", key: "tasks" },
      { label: "", sortable: false },
    ], rows.length ? rows : [el("tr", {}, [el("td", { class: "px-3 py-4 text-gray-400", colspan: "9", text: filter === "open" ? "No open meeting minutes." : "No meeting minutes for this filter." })])], sortState, save, rerender),
  ]);
}

function taskSortableTh(el, label, colKey, sortState, persist, rerender) {
  const active = sortState?.key === colKey;
  const arrow = active ? (sortState.dir === "asc" ? " ▲" : " ▼") : "";
  return el("th", {
    class: "px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-[#676879] cursor-pointer select-none hover:text-[#0073ea]",
    text: `${label}${arrow}`,
    onclick: () => {
      if (!sortState) return;
      toggleSortState(sortState, colKey);
      persist?.();
      rerender();
    },
  });
}

function taskSortValue(t, key, allTasks, minutes) {
  switch (key) {
    case "number": return t.number || "";
    case "title": return t.title || "";
    case "asset": return t.asset || "";
    case "owner": return t.owner || "";
    case "start": return parseDateMs(t.startDate) ?? "";
    case "target": return parseDateMs(t.targetDate) ?? "";
    case "status": return effectiveTaskStatus(t, allTasks);
    case "priority": return t.priority || "normal";
    case "deps": return (t.dependsOn || []).join(",");
    case "minute": {
      const m = (minutes || []).find((x) => x.id === t.linkedMinuteId);
      return m ? `${m.number} ${m.subTopic || m.principalTopic || ""}` : "";
    }
    case "notes": return (t.comments || []).length;
    default: return t.number || "";
  }
}

function sortTaskRows(tasks, sortState, allTasks, minutes) {
  const key = sortState?.key || "number";
  const dir = sortState?.dir === "desc" ? -1 : 1;
  return [...tasks].sort((a, b) => {
    if (key === "number") return compareItemNumbers(a.number, b.number) * dir;
    const av = taskSortValue(a, key, allTasks, minutes);
    const bv = taskSortValue(b, key, allTasks, minutes);
    if (typeof av === "number" && typeof bv === "number" && !Number.isNaN(av) && !Number.isNaN(bv)) {
      return (av - bv) * dir;
    }
    return String(av).localeCompare(String(bv), undefined, { numeric: true }) * dir;
  });
}

function filterTaskRows(tasks, filter, allTasks, minutes = []) {
  const f = filter || {};
  const status = String(f.status || "all").toLowerCase();
  const priority = String(f.priority || "all").toLowerCase();
  const text = String(f.text || "").trim().toLowerCase();
  return (tasks || []).filter((t) => {
    const eff = effectiveTaskStatus(t, allTasks);
    const stored = String(t.status || "").toLowerCase() || eff;
    if (status !== "all" && stored !== status && eff !== status) return false;
    if (priority !== "all" && (t.priority || "normal") !== priority) return false;
    if (text) {
      const minute = (minutes || []).find((x) => x.id === t.linkedMinuteId);
      const hay = [
        t.number, t.title, t.asset, t.owner, t.startDate, t.targetDate,
        (t.dependsOn || []).join(","),
        minute?.number, minute?.subTopic,
      ].join(" ").toLowerCase();
      if (!hay.includes(text)) return false;
    }
    return true;
  });
}

function tasksForTreeView(allTasks, filter, minutes) {
  const matched = filterTaskRows(allTasks, filter, allTasks, minutes);
  const include = new Set(matched.map((t) => t.id));
  for (const t of matched) {
    let p = taskText(t.parentNumber);
    while (p) {
      const parent = allTasks.find((x) => x.number === p);
      if (!parent) break;
      include.add(parent.id);
      p = taskText(parent.parentNumber);
    }
  }
  return allTasks.filter((t) => include.has(t.id));
}

function taskChildren(tasks, parentNumber) {
  const p = taskText(parentNumber);
  return tasks
    .filter((t) => taskText(t.parentNumber) === p)
    .sort((a, b) => compareItemNumbers(a.number, b.number));
}

function taskChildrenSorted(tasks, parentNumber, sortState, allTasks, minutes) {
  const kids = taskChildren(tasks, parentNumber);
  const key = sortState?.key || "number";
  if (key === "number" || key === "title") {
    return sortState?.dir === "desc" ? [...kids].reverse() : kids;
  }
  return sortTaskRows(kids, sortState, allTasks, minutes);
}

function walkTaskTree(tasks, collapsed, parentNumber, depth, visit, sortState, allTasks, minutes) {
  const siblings = taskChildrenSorted(tasks, parentNumber, sortState, allTasks, minutes);
  for (const t of siblings) {
    visit(t, depth);
    const kids = taskChildrenSorted(tasks, t.number, sortState, allTasks, minutes);
    if (kids.length && !collapsed.has(t.id)) {
      walkTaskTree(tasks, collapsed, t.number, depth + 1, visit, sortState, allTasks, minutes);
    }
  }
}

function taskRootCategoryNumber(t, allTasks) {
  let num = t.number;
  let p = taskText(t.parentNumber);
  while (p) {
    num = p;
    const parent = allTasks.find((x) => x.number === p);
    p = parent ? taskText(parent.parentNumber) : "";
  }
  return num;
}

function countTaskDescendants(t, tasks) {
  const countKids = (num) => {
    const kids = tasks.filter((x) => taskText(x.parentNumber) === num);
    return kids.reduce((n, c) => n + 1 + countKids(c.number), 0);
  };
  return countKids(t.number);
}

const MONDAY_GROUP_ACCENTS = [
  "border-l-[#5034ff]",
  "border-l-[#00c875]",
  "border-l-[#fdab3d]",
  "border-l-[#e2445c]",
  "border-l-[#579bfc]",
  "border-l-[#a25ddc]",
];

function mondayGroupAccent(t, depth, topLevel, allTasks) {
  const rootNum = depth === 0 ? t.number : taskRootCategoryNumber(t, allTasks);
  const idx = topLevel.findIndex((x) => x.number === rootNum);
  return MONDAY_GROUP_ACCENTS[(idx >= 0 ? idx : 0) % MONDAY_GROUP_ACCENTS.length];
}

function mondayIconBtn(el, { title, svg, onClick, className = "" }) {
  const btn = el("button", {
    type: "button",
    class: `inline-flex items-center justify-center w-7 h-7 rounded-md text-[#676879] hover:bg-[#cce5ff] hover:text-[#0073ea] transition-colors shrink-0 opacity-80 group-hover:opacity-100 ${className}`,
    title,
    onclick: (e) => {
      e.stopPropagation();
      onClick();
    },
  });
  btn.innerHTML = svg;
  return btn;
}

function mondayCaretBtn(el, { expanded, onClick }) {
  return el("button", {
    type: "button",
    class: "inline-flex items-center justify-center w-7 h-7 rounded-md text-[#676879] hover:bg-[#e6e9ef] shrink-0 text-sm",
    title: expanded ? "Collapse" : "Expand",
    text: expanded ? "▾" : "▸",
    onclick: (e) => {
      e.stopPropagation();
      onClick();
    },
  });
}

function mondayStatusPill(el, progressBadge, value, eff, metaFn) {
  const m = metaFn(value || eff);
  if (progressBadge) return progressBadge(m.label, m.variant);
  const pillClass = {
    gray: "bg-[#e6e9ef] text-[#323338]",
    blue: "bg-[#cce5ff] text-[#0073ea]",
    amber: "bg-[#fff3d6] text-[#c77c00]",
    green: "bg-[#d0f0d4] text-[#037f4c]",
    red: "bg-[#ffd4d4] text-[#bb3354]",
  }[m.variant] || "bg-[#e6e9ef] text-[#323338]";
  return el("span", {
    class: `inline-flex items-center px-2.5 py-1 rounded-md text-[11px] font-medium whitespace-nowrap ${pillClass}`,
    text: m.label,
  });
}

function renderTasksSection(ctx, project, projectPrefs, persist) {
  const { el, persist: ctxPersist, rerender, forceRerender, progressBadge, clickEditCell, clickEditDateCell, cleanText } = ctx;
  const save = persist || ctxPersist;
  const refresh = forceRerender || rerender;
  const allTasks = project.tasks || [];
  const minutes = project.minutes || [];
  const uiPrefs = projectPrefs || {};
  if (!uiPrefs.taskSort) uiPrefs.taskSort = { key: "number", dir: "asc" };
  if (uiPrefs.taskSort.key === "title") uiPrefs.taskSort = { key: "number", dir: uiPrefs.taskSort.dir || "asc" };
  if (!uiPrefs.taskFilter) uiPrefs.taskFilter = { status: "all", priority: "all", text: "" };
  if (!Array.isArray(uiPrefs.taskCollapsed)) uiPrefs.taskCollapsed = [];
  const collapsed = new Set(uiPrefs.taskCollapsed);
  let dragTaskId = null;

  const reorderDraggedTask = (targetTask) => {
    if (!dragTaskId || dragTaskId === targetTask.id) return;
    const dragged = allTasks.find((x) => x.id === dragTaskId);
    if (!dragged) return;
    if (taskText(dragged.parentNumber) !== taskText(targetTask.parentNumber)) return;
    const pNum = taskText(dragged.parentNumber);
    const ids = taskChildren(allTasks, pNum).map((s) => s.id);
    const fromIdx = ids.indexOf(dragTaskId);
    const toIdx = ids.indexOf(targetTask.id);
    if (fromIdx < 0 || toIdx < 0) return;
    ids.splice(fromIdx, 1);
    ids.splice(toIdx, 0, dragTaskId);
    applySiblingOrder(allTasks, pNum, ids);
    uiPrefs.taskSort = { key: "number", dir: "asc" };
    dragTaskId = null;
    save();
    refresh();
  };

  const treeTasks = tasksForTreeView(allTasks, uiPrefs.taskFilter, minutes);
  const topLevel = taskChildren(treeTasks, "");
  const visibleRows = [];
  walkTaskTree(treeTasks, collapsed, "", 0, (t, depth) => visibleRows.push({ t, depth }), uiPrefs.taskSort, allTasks, minutes);

  const toggleCollapsed = (taskId) => {
    if (collapsed.has(taskId)) collapsed.delete(taskId);
    else collapsed.add(taskId);
    uiPrefs.taskCollapsed = [...collapsed];
    save();
    refresh();
  };

  const collapseAll = () => {
    uiPrefs.taskCollapsed = treeTasks
      .filter((t) => taskChildren(treeTasks, t.number).length > 0)
      .map((t) => t.id);
    save();
    refresh();
  };

  const expandAll = () => {
    uiPrefs.taskCollapsed = [];
    save();
    refresh();
  };

  const addTask = (parentNumber = "") => {
    const pNum = taskText(parentNumber);
    const num = nextTaskNumber(pNum, allTasks);
    project.tasks.push({
      id: newId("tsk"), number: num, parentNumber: pNum, title: "", asset: "", owner: "",
      status: "not_started", priority: "normal", startDate: "", targetDate: "",
      dependsOn: [], linkedMinuteId: null, comments: [],
    });
    uiPrefs.taskSort = { key: "number", dir: "asc" };
    if (pNum) {
      const parent = allTasks.find((x) => x.number === pNum);
      if (parent?.id) collapsed.delete(parent.id);
      uiPrefs.taskCollapsed = [...collapsed];
    }
    save();
    refresh();
  };

  const minuteOpts = [{ value: "", label: "—" }, ...minutes.map((m) => ({
    value: m.id,
    label: `${m.number} ${truncateText(m.subTopic || m.principalTopic, 40)}`,
  }))];
  const statusFilterOpts = [{ value: "all", label: "All statuses" }, ...TASK_STATUSES.map((s) => ({ value: s.value, label: s.label }))];
  const priorityFilterOpts = [{ value: "all", label: "All priorities" }, ...TASK_PRIORITIES.map((p) => ({ value: p.value, label: p.label }))];

  const renderRow = ({ t, depth }, rowIndex) => {
    const eff = effectiveTaskStatus(t, allTasks);
    const kids = taskChildren(treeTasks, t.number);
    const hasKids = kids.length > 0;
    const isGroup = depth === 0;
    const expanded = !collapsed.has(t.id);
    const descCount = countTaskDescendants(t, allTasks);
    const accent = mondayGroupAccent(t, depth, topLevel, allTasks);
    const groupDivider = isGroup && rowIndex > 0 ? "border-t-2 border-[#e6e9ef]" : "";

    const startCell = clickEditDateCell
      ? clickEditDateCell({ value: t.startDate, className: "px-2 py-2.5", onCommit: (v) => { t.startDate = v; persist(); } })
      : textCell(ctx, t.startDate, (v) => { t.startDate = v; persist(); }, { className: "px-2 py-2.5 text-[#676879]" });
    const targetCell = clickEditDateCell
      ? clickEditDateCell({ value: t.targetDate, className: "px-2 py-2.5", onCommit: (v) => { t.targetDate = v; persist(); } })
      : textCell(ctx, t.targetDate, (v) => { t.targetDate = v; persist(); }, { className: "px-2 py-2.5 text-[#676879]" });
    const statusCell = clickEditCell
      ? clickEditCell({
          value: t.status || eff,
          className: "px-2 py-2.5",
          selectOptions: TASK_STATUSES.map((s) => ({ value: s.value, label: s.label })),
          renderDisplay: (v) => mondayStatusPill(el, progressBadge, v, eff, statusMeta),
          onCommit: (v) => { t.status = v; persist(); },
        })
      : el("td", { class: "px-2 py-2.5" }, [mondayStatusPill(el, progressBadge, t.status, eff, statusMeta)]);
    const priorityCell = clickEditCell
      ? clickEditCell({
          value: t.priority || "normal",
          className: "px-2 py-2.5",
          selectOptions: TASK_PRIORITIES.map((p) => ({ value: p.value, label: p.label })),
          renderDisplay: (v) => mondayStatusPill(el, progressBadge, v, "normal", priorityMeta),
          onCommit: (v) => { t.priority = v; persist(); },
        })
      : el("td", { class: "px-2 py-2.5" }, [mondayStatusPill(el, progressBadge, t.priority, "normal", priorityMeta)]);
    const minuteCell = clickEditCell
      ? clickEditCell({ value: t.linkedMinuteId || "", className: "px-2 py-2.5 max-w-[140px] text-[#676879]", selectOptions: minuteOpts, onCommit: (v) => { t.linkedMinuteId = v || null; persist(); } })
      : el("td", { class: "px-2 py-2.5 text-[#676879]", text: "—" });

    const addLabel = isGroup ? "Add sub-item" : "Add sibling";
    const onAdd = () => addTask(isGroup ? t.number : taskText(t.parentNumber));
    const placeholder = isGroup ? "Category name" : "Item name";

    const titleCell = clickEditCell
      ? clickEditCell({
          value: t.title || "",
          className: "flex-1 min-w-0 px-0 py-0 align-middle",
          renderDisplay: (v) => el("div", { class: "flex items-center gap-2 min-w-0" }, [
            el("span", {
              class: "shrink-0 px-1.5 py-0.5 rounded text-[10px] font-mono text-[#676879] bg-[#e6e9ef]/70",
              text: t.number || "—",
            }),
            el("span", {
              class: `truncate ${isGroup ? "font-semibold text-[13px] text-[#323338]" : "text-[13px] text-[#323338]"}`,
              text: v || placeholder,
            }),
            hasKids && !expanded
              ? el("span", {
                class: "shrink-0 text-[10px] font-medium text-[#676879] bg-[#e6e9ef] px-2 py-0.5 rounded-full",
                text: `${descCount} item${descCount === 1 ? "" : "s"}`,
              })
              : null,
          ].filter(Boolean)),
          onCommit: (v) => { t.title = v; persist(); },
        })
      : el("td", { class: "flex-1", text: t.title || "—" });

    titleCell.className = `min-w-[300px] align-middle border-l-[3px] ${accent} ${isGroup ? "bg-white" : "bg-[#fafbfc]"} px-3 py-2.5`;
    const itemWrap = el("div", {
      class: "flex items-center gap-0.5 min-w-0 w-full",
      style: { paddingLeft: `${depth * 20}px` },
    });
    const gripBtn = el("button", {
      type: "button",
      class: "inline-flex items-center justify-center w-6 h-7 rounded-md text-[#c5c7d0] hover:text-[#676879] hover:bg-[#e6e9ef] shrink-0 cursor-grab active:cursor-grabbing",
      title: "Drag to reorder",
      draggable: "true",
      ondragstart: (ev) => {
        dragTaskId = t.id;
        ev.dataTransfer.effectAllowed = "move";
        ev.dataTransfer.setData("text/plain", t.id);
        ev.currentTarget.closest("tr")?.classList.add("opacity-50");
      },
      ondragend: (ev) => {
        dragTaskId = null;
        ev.currentTarget.closest("tr")?.classList.remove("opacity-50");
        document.querySelectorAll("[data-task-drop-target]").forEach((n) => n.classList.remove("ring-2", "ring-[#0073ea]"));
      },
    });
    gripBtn.innerHTML = ICON_GRIP;
    itemWrap.appendChild(gripBtn);
    itemWrap.appendChild(mondayIconBtn(el, { title: addLabel, svg: ICON_PLUS, onClick: onAdd }));
    itemWrap.appendChild(hasKids
      ? mondayCaretBtn(el, { expanded, onClick: () => toggleCollapsed(t.id) })
      : el("span", { class: "w-7 shrink-0" }));
    const editWrap = el("div", { class: "flex-1 min-w-0" });
    while (titleCell.firstChild) editWrap.appendChild(titleCell.firstChild);
    itemWrap.appendChild(editWrap);
    titleCell.appendChild(itemWrap);

    const noteCount = (t.comments || []).length;
    const addTaskNote = () => openCommentModal(el, {
      title: "Task comment",
      onSubmit: (text) => {
        t.comments.push({ id: newId("c"), text, at: new Date().toISOString(), by: "user" });
        persist();
        refresh();
      },
    });

    const rowClass = isGroup
      ? `group ${groupDivider} bg-white hover:bg-[#f5f6f8] transition-colors`
      : "group bg-[#fafbfc] hover:bg-[#f0f1f5] transition-colors";

    return el("tr", {
      class: rowClass,
      "data-task-id": t.id,
      "data-task-parent": taskText(t.parentNumber),
      ondragover: (ev) => {
        if (!dragTaskId || dragTaskId === t.id) return;
        const dragged = allTasks.find((x) => x.id === dragTaskId);
        if (!dragged || taskText(dragged.parentNumber) !== taskText(t.parentNumber)) return;
        ev.preventDefault();
        ev.dataTransfer.dropEffect = "move";
        ev.currentTarget.classList.add("ring-2", "ring-[#0073ea]");
        ev.currentTarget.setAttribute("data-task-drop-target", "1");
      },
      ondragleave: (ev) => {
        ev.currentTarget.classList.remove("ring-2", "ring-[#0073ea]");
        ev.currentTarget.removeAttribute("data-task-drop-target");
      },
      ondrop: (ev) => {
        ev.preventDefault();
        ev.currentTarget.classList.remove("ring-2", "ring-[#0073ea]");
        ev.currentTarget.removeAttribute("data-task-drop-target");
        reorderDraggedTask(t);
      },
    }, [
      titleCell,
      textCell(ctx, t.owner || "", (v) => { t.owner = v; persist(); }, { className: "px-2 py-2.5 text-[#676879] min-w-[90px]" }),
      textCell(ctx, t.asset || "", (v) => { t.asset = v; persist(); }, { className: "px-2 py-2.5 text-[#676879] min-w-[90px]" }),
      startCell,
      targetCell,
      statusCell,
      priorityCell,
      minuteCell,
      el("td", { class: "px-2 py-2.5 text-[#676879] text-[11px] whitespace-nowrap" }, [
        noteCount
          ? el("span", { text: `${noteCount} note${noteCount > 1 ? "s" : ""}` })
          : el("span", { class: "opacity-0 group-hover:opacity-100 text-[#c5c7d0]", text: "—" }),
      ]),
      el("td", { class: "px-2 py-2.5 text-right whitespace-nowrap" }, [
        el("div", { class: "inline-flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity" }, [
          mondayIconBtn(el, { title: "Add note", svg: ICON_COMMENT, onClick: addTaskNote, className: "!w-6 !h-6" }),
          mondayIconBtn(el, {
            title: "Delete",
            svg: ICON_TRASH,
            className: "!w-6 !h-6 hover:!bg-[#ffd4d4] hover:!text-[#bb3354]",
            onClick: () => confirmDelete(`Delete task ${t.number}?`, () => {
              const removeNums = new Set([t.number]);
              const gather = (num) => {
                for (const c of allTasks.filter((x) => x.parentNumber === num)) {
                  removeNums.add(c.number);
                  gather(c.number);
                }
              };
              gather(t.number);
              project.tasks = project.tasks.filter((x) => !removeNums.has(x.number));
              persist();
              rerenderAfterStructuralChange(ctx);
            }),
          }),
        ]),
      ]),
    ]);
  };

  const taskCols = [
    ["Item #", "number"], ["Owner", "owner"], ["Asset", "asset"],
    ["Start", "start"], ["Due", "target"], ["Status", "status"], ["Priority", "priority"],
    ["Minute", "minute"], ["Notes", "notes"],
  ];

  const statusFilter = el("select", {
    class: "text-xs border border-[#c5c7d0] rounded-md px-2.5 py-1.5 bg-white text-[#323338]",
    value: uiPrefs.taskFilter.status || "all",
    onchange: (ev) => {
      uiPrefs.taskFilter.status = cleanText(ev.target.value) || "all";
      save();
      rerender();
    },
  }, statusFilterOpts.map((o) => el("option", { value: o.value, text: o.label, selected: o.value === (uiPrefs.taskFilter.status || "all") })));

  const priorityFilter = el("select", {
    class: "text-xs border border-[#c5c7d0] rounded-md px-2.5 py-1.5 bg-white text-[#323338]",
    value: uiPrefs.taskFilter.priority || "all",
    onchange: (ev) => {
      uiPrefs.taskFilter.priority = cleanText(ev.target.value) || "all";
      save();
      rerender();
    },
  }, priorityFilterOpts.map((o) => el("option", { value: o.value, text: o.label, selected: o.value === (uiPrefs.taskFilter.priority || "all") })));

  const textFilter = el("input", {
    type: "search",
    class: "text-xs border border-[#c5c7d0] rounded-md px-2.5 py-1.5 min-w-[160px] bg-white text-[#323338] placeholder:text-[#c5c7d0]",
    placeholder: "Search items…",
    value: uiPrefs.taskFilter.text || "",
    "data-op-block-rerender": "1",
    oninput: (ev) => {
      uiPrefs.taskFilter.text = cleanText(ev.target.value);
      save();
      clearTimeout(uiPrefs._taskFilterTimer);
      uiPrefs._taskFilterTimer = setTimeout(() => refresh(), 350);
    },
    onblur: () => {
      clearTimeout(uiPrefs._taskFilterTimer);
      rerender();
    },
  });

  const rows = visibleRows.map((row, i) => renderRow(row, i));

  return el("section", { class: "mb-6", "data-project-id": project.id }, [
    el("div", { class: "rounded-xl border border-[#d0d4e4] bg-[#f6f7fb] overflow-hidden shadow-sm" }, [
      el("div", { class: "flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-[#d0d4e4] bg-white" }, [
        el("div", {}, [
          el("h2", { class: "text-[15px] font-semibold text-[#323338]", text: `Task schedule · ${project.title || "Untitled project"}` }),
          el("p", { class: "text-[11px] text-[#676879] mt-0.5", text: "Drag ⋮⋮ to reorder · # sorts by number · Start/Due sort within each group" }),
        ]),
        el("div", { class: "flex flex-wrap items-center gap-2" }, [
          textFilter,
          statusFilter,
          priorityFilter,
          el("span", { class: "text-[11px] text-[#676879] px-1", text: `${visibleRows.length} / ${allTasks.length}` }),
          el("button", {
            type: "button",
            class: "text-xs px-2.5 py-1.5 rounded-md border border-[#c5c7d0] bg-white text-[#676879] hover:bg-[#f5f6f8]",
            text: "Expand all",
            onclick: expandAll,
          }),
          el("button", {
            type: "button",
            class: "text-xs px-2.5 py-1.5 rounded-md border border-[#c5c7d0] bg-white text-[#676879] hover:bg-[#f5f6f8]",
            text: "Collapse all",
            onclick: collapseAll,
          }),
          el("button", {
            type: "button",
            class: "text-xs px-3 py-1.5 rounded-md bg-[#0073ea] text-white font-medium hover:bg-[#0060b9]",
            text: "+ New category",
            onclick: () => addTask(""),
          }),
        ]),
      ]),
      el("div", { class: "overflow-x-auto bg-white" }, [
        el("table", { class: "min-w-[1000px] w-full text-left text-xs" }, [
          el("thead", {}, [
            el("tr", { class: "bg-[#f6f7fb] border-b border-[#d0d4e4]" }, [
              ...taskCols.map(([label, key]) => taskSortableTh(el, label, key, uiPrefs.taskSort, save, rerender)),
              el("th", { class: "px-2 py-2.5 w-16" }),
            ]),
          ]),
          el("tbody", {}, rows.length ? rows : [
            el("tr", {}, [
              el("td", {
                class: "px-4 py-12 text-center text-[#676879] text-sm",
                colspan: String(taskCols.length + 1),
                text: allTasks.length ? "No items match this filter." : "No categories yet — click + New category to start.",
              }),
            ]),
          ]),
        ]),
      ]),
    ]),
  ]);
}

function openCommentModal(el, { title = "Add comment", onSubmit }) {
  openPromptModal(el, {
    title,
    fields: [{ id: "text", label: "Comment", inputType: "textarea", placeholder: "Enter comment…" }],
    onSubmit: (values) => {
      const text = values.text?.trim();
      if (!text) return false;
      onSubmit(text);
    },
  });
}

function openPromptModal(el, { title, fields, submitLabel = "Save", onSubmit }) {
  const backdrop = el("div", {
    class: "fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4",
    "data-actracker-modal-backdrop": "1",
  });
  const modal = el("div", { class: "w-full max-w-md rounded-xl bg-white shadow-lg border border-gray-200 p-5" });
  const inputs = {};
  const body = el("div", { class: "space-y-3 my-4" });
  for (const field of fields) {
    const input = field.inputType === "textarea"
      ? el("textarea", {
          rows: field.rows || 4,
          class: "w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-800",
          value: field.value || "",
          placeholder: field.placeholder || "",
        })
      : el("input", {
          type: field.type || "text",
          class: "w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-800",
          value: field.value || "",
          placeholder: field.placeholder || "",
        });
    inputs[field.id] = input;
    body.appendChild(el("label", { class: "block text-xs font-medium text-gray-600" }, [
      el("span", { text: field.label }),
      input,
    ]));
  }
  const close = () => backdrop.remove();
  modal.appendChild(el("h3", { class: "text-lg font-semibold text-gray-900", text: title }));
  modal.appendChild(body);
  modal.appendChild(el("div", { class: "flex justify-end gap-2" }, [
    el("button", { type: "button", class: "px-3 py-2 text-sm text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50", text: "Cancel", onclick: close }),
    el("button", {
      type: "button",
      class: "px-3 py-2 text-sm text-white bg-dae rounded-lg hover:bg-dae/90",
      text: submitLabel,
      onclick: () => {
        const values = Object.fromEntries(Object.entries(inputs).map(([k, inp]) => [k, String(inp.value || "").trim()]));
        if (onSubmit(values, close) !== false) close();
      },
    }),
  ]));
  backdrop.appendChild(modal);
  backdrop.addEventListener("click", (ev) => { if (ev.target === backdrop) close(); });
  document.body.appendChild(backdrop);
  const first = Object.values(inputs)[0];
  if (first) setTimeout(() => first.focus(), 0);
}

function attachmentOpenHref(a, apiUrl) {
  if (a.kind === "link") return a.url || "";
  if (a.fileId && apiUrl) return apiUrl(`/api/operator-progress/attachments/${a.fileId}`);
  return "";
}

function renderAttachmentCommentsCell(ctx, att, persist, rerender) {
  const { el } = ctx;
  const comments = sortCommentsNewestFirst(att.comments);
  const latest = comments[0];
  const addComment = () => openCommentModal(el, {
    title: "Add comment",
    onSubmit: (text) => {
      if (!Array.isArray(att.comments)) att.comments = [];
      att.comments.unshift({ id: newId("c"), text, at: new Date().toISOString(), by: "user" });
      persist(); rerender();
    },
  });
  if (!latest) {
    return el("td", { class: "px-2 py-1 align-top text-gray-400 max-w-[200px]" }, [
      actionLink(el, "Add comment", addComment, { icon: ICON_COMMENT, title: "Add comment" }),
    ]);
  }
  return el("td", { class: "px-2 py-1 align-top max-w-[200px]" }, [
    el("details", {}, [
      el("summary", { class: "cursor-pointer text-gray-800 hover:text-dae list-none", text: truncateText(latest.text, 48) || "—" }),
      el("div", { class: "mt-1 space-y-1 border-t border-gray-100 pt-1" }, [
        ...comments.map((c) => el("div", { class: "text-[11px] text-gray-600 leading-snug", text: `${fmtWhen(c.at)} — ${c.text}` })),
        actionLink(el, "Add comment", addComment, { icon: ICON_COMMENT, title: "Add comment" }),
      ]),
    ]),
  ]);
}

function linkIcon(el, href, title) {
  if (!href) return el("span", { class: "text-gray-300", text: "—" });
  const a = el("a", {
    class: "inline-flex items-center justify-center w-7 h-7 rounded-lg border border-gray-200 text-dae hover:bg-red-50",
    href,
    target: "_blank",
    rel: "noopener noreferrer",
    title,
  });
  a.innerHTML = ICON_LINK;
  return a;
}

function fileIcon(el, href, title, preview) {
  if (!href) return el("span", { class: "text-gray-300", text: "—" });
  const a = el("a", {
    class: "inline-flex items-center justify-center w-7 h-7 rounded-lg border border-gray-200 text-dae hover:bg-red-50",
    href,
    target: "_blank",
    rel: "noopener noreferrer",
    title,
  });
  a.innerHTML = preview
    ? '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-4 h-4"><path d="M10 12.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z"/><path fill-rule="evenodd" d="M.664 10.59a1.651 1.651 0 0 1 .213-1.082l1.378-2.406A1.65 1.65 0 0 1 3.09 6.5h13.82c.65 0 1.223.388 1.47.99l1.378 2.406c.232.406.232.878-.001 1.284l-1.378 2.406a1.65 1.65 0 0 1-1.47.99H3.09a1.65 1.65 0 0 1-1.47-.99l-1.378-2.406a1.651 1.651 0 0 1-.178-1.082Zm1.99-.59h15.692l-1.125 1.97H3.78l-1.126-1.97Z" clip-rule="evenodd"/></svg>'
    : '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-4 h-4"><path d="M10.75 2.75a.75.75 0 0 0-1.5 0v8.614L6.295 8.235a.75.75 0 1 0-1.09 1.03l4.25 4.5a.75.75 0 0 0 1.09 0l4.25-4.5a.75.75 0 1 0-1.09-1.03l-2.955 3.129V2.75Z"/><path d="M3.5 12.75a.75.75 0 0 0-1.5 0v2.5A2.75 2.75 0 0 0 4.75 18h10.5A2.75 2.75 0 0 0 18 15.25v-2.5a.75.75 0 0 0-1.5 0v2.5c0 .69-.56 1.25-1.25 1.25H4.75c-.69 0-1.25-.56-1.25-1.25v-2.5Z"/></svg>';
  return a;
}

function renderAttachmentsSection(ctx, project, projectPrefs, persist) {
  const { el, persist: ctxPersist, rerender, operatorId, fetchJson, apiUrl } = ctx;
  const save = persist || ctxPersist;
  const sortState = projectPrefs?.attachmentsSort || { key: "label", dir: "asc" };
  const attachmentSortValue = (a, key) => {
    if (key === "label") return a.label || "";
    if (key === "kind") return a.kind || "";
    if (key === "created") return parseDateMs(a.createdAt) ?? 0;
    return a.label || "";
  };

  const uploadFiles = async (files, defaultName = "") => {
    if (!files?.length || !operatorId || !fetchJson || !apiUrl) return;
    for (const f of files) {
      const name = defaultName || f.name;
      const reader = new FileReader();
      await new Promise((resolve) => {
        reader.onload = async () => {
          const resp = await fetchJson(apiUrl(`/api/operator-progress/${operatorId}/attachments`), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ projectId: project.id, fileName: f.name, mime: f.type || "application/octet-stream", base64: String(reader.result || "") }),
          });
          if (!resp?.ok) {
            window.alert(resp?.error || `Upload failed: ${f.name}`);
            resolve();
            return;
          }
          const att = resp.attachment;
          project.attachments.push({
            id: newId("att"),
            kind: "file",
            label: name,
            url: "",
            fileId: att.id,
            mime: att.mime,
            createdAt: new Date().toISOString(),
            comments: [],
          });
          resolve();
        };
        reader.readAsDataURL(f);
      });
    }
    persist(); rerender();
  };

  const addLink = () => {
    openPromptModal(el, {
      title: "Add link",
      fields: [
        { id: "name", label: "Name", placeholder: "Document title" },
        { id: "url", label: "URL", type: "url", placeholder: "https://…" },
      ],
      onSubmit: (values) => {
        if (!values.url) { window.alert("URL is required"); return false; }
        project.attachments.push({
          id: newId("att"),
          kind: "link",
          label: values.name || "Link",
          url: values.url,
          fileId: null,
          mime: null,
          createdAt: new Date().toISOString(),
          comments: [],
        });
        persist(); rerender();
      },
    });
  };

  const uploadFile = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.onchange = () => {
      const f = input.files?.[0];
      if (!f) return;
      openPromptModal(el, {
        title: "Upload file",
        fields: [{ id: "name", label: "Display name", value: f.name }],
        onSubmit: (values) => { uploadFiles([f], values.name || f.name); },
      });
    };
    input.click();
  };

  const onDrop = async (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    ev.currentTarget?.classList?.remove("ring-2", "ring-dae/40");
    const files = [...(ev.dataTransfer?.files || [])];
    if (files.length) await uploadFiles(files);
  };

  const rows = sortItems(project.attachments || [], sortState, attachmentSortValue).map((a, idx) => {
    const href = attachmentOpenHref(a, apiUrl);
    const icon = a.kind === "link"
      ? linkIcon(el, href, "Open link")
      : fileIcon(el, href, isOfficePreview(a.mime, a.label) ? "Preview file" : "Download file", isOfficePreview(a.mime, a.label));
    return el("tr", { class: `border-t border-gray-100 ${zebraRow(idx)}` }, [
      textCell(ctx, a.label || "", (v) => { a.label = v; persist(); }, { className: "px-2 py-1 font-medium text-gray-800 max-w-[200px]" }),
      el("td", { class: "px-2 py-1 text-gray-500 uppercase whitespace-nowrap", text: a.kind === "link" ? "Link" : "File" }),
      el("td", { class: "px-2 py-1 text-gray-600 whitespace-nowrap", text: fmtWhen(a.createdAt).split(",")[0] || "—" }),
      el("td", { class: "px-2 py-1" }, [href ? icon : el("span", { class: "text-gray-300", text: "—" })]),
      renderAttachmentCommentsCell(ctx, a, persist, rerender),
      el("td", { class: "px-2 py-1 text-right whitespace-nowrap" }, [
        actionLink(el, "Remove", () => confirmDelete(`Remove attachment "${a.label || "item"}"?`, () => {
          const i = project.attachments.indexOf(a);
          if (i >= 0) project.attachments.splice(i, 1);
          persist(); rerender();
        }), { danger: true, icon: ICON_TRASH, title: "Remove attachment" }),
      ]),
    ]);
  });

  return el("section", {
    class: "mb-8",
    ondragover: (ev) => { ev.preventDefault(); ev.currentTarget.classList.add("ring-2", "ring-dae/40"); },
    ondragleave: (ev) => { ev.currentTarget.classList.remove("ring-2", "ring-dae/40"); },
    ondrop: onDrop,
  }, [
    sectionBar(ctx, "N · Attachments", [
      actionLink(el, "Add link", addLink, { icon: ICON_LINK, title: "Add link" }),
      actionLink(el, "Upload file", uploadFile, { icon: ICON_PLUS, title: "Upload file" }),
    ]),
    el("p", { class: "text-[11px] text-gray-400 mb-2", text: "Drag files here to upload. Links open via icon only." }),
    progressSortableTable(el, [
      { label: "Name", key: "label" },
      { label: "Type", key: "kind" },
      { label: "Created", key: "created" },
      { label: "", sortable: false },
      { label: "Comments", sortable: false },
      { label: "", sortable: false },
    ], rows.length ? rows : [el("tr", {}, [el("td", { class: "px-3 py-4 text-gray-400", colspan: "6", text: "No attachments yet." })])], sortState, save, rerender),
  ]);
}

export function buildProjectsOutlookHtml(project, sections, htmlEscape, outlookSectionTitle) {
  if (!project) return "";
  const parts = [];
  const on = (id) => sections?.[id] === true;

  if (on("projectSummary")) {
    parts.push(outlookSectionTitle("J · Project summary"));
    parts.push(`<p><strong>${htmlEscape(project.title)}</strong> (${htmlEscape(project.type || "general")})</p>`);
    if (project.summary) parts.push(`<p>${htmlEscape(project.summary)}</p>`);
    if (project.criticalItems?.length) {
      parts.push("<ul>" + project.criticalItems.map((x) => `<li>${htmlEscape(x)}</li>`).join("") + "</ul>");
    }
  }

  if (on("contacts") && project.contacts?.length) {
    parts.push(outlookSectionTitle("K · Contacts"));
    parts.push("<table border='1' cellpadding='4' cellspacing='0' style='border-collapse:collapse;font-size:12px;'><tr><th>Name</th><th>Email</th><th>Phone</th><th>Company</th><th>Position</th></tr>");
    for (const c of project.contacts) {
      const name = [c.firstName, c.lastName].filter(Boolean).join(" ");
      parts.push(`<tr><td>${htmlEscape(name)}</td><td>${htmlEscape(c.email)}</td><td>${htmlEscape(c.phone)}</td><td>${htmlEscape(c.company)}</td><td>${htmlEscape(c.position)}</td></tr>`);
    }
    parts.push("</table>");
  }

  // Meeting minutes (L) are rendered separately via buildMinutesOutlookHtml so they can sit last in the email.

  if (on("tasks") && project.tasks?.length) {
    parts.push(outlookSectionTitle("M · Task schedule"));
    parts.push("<table border='1' cellpadding='4' cellspacing='0' style='border-collapse:collapse;font-size:12px;'><tr><th>#</th><th>Task</th><th>Start</th><th>Target</th><th>Status</th><th>Priority</th></tr>");
    for (const t of sortedTasks(project.tasks)) {
      const eff = effectiveTaskStatus(t, project.tasks);
      parts.push(`<tr><td>${htmlEscape(t.number)}</td><td>${htmlEscape(t.title)}</td><td>${htmlEscape(t.startDate)}</td><td>${htmlEscape(t.targetDate)}</td><td>${htmlEscape(statusMeta(eff).label)}</td><td>${htmlEscape(priorityMeta(t.priority).label)}</td></tr>`);
    }
    parts.push("</table>");
  }

  if (on("attachments") && project.attachments?.length) {
    parts.push(outlookSectionTitle("N · Attachments"));
    parts.push("<ul>" + project.attachments.map((a) =>
      `<li>${htmlEscape(a.label)}${a.url ? ` — <a href="${htmlEscape(a.url)}">${htmlEscape(a.url)}</a>` : " (file)"}</li>`
    ).join("") + "</ul>");
  }

  return parts.join("\n");
}

function trackerPriorityLabel(value) {
  const v = String(value || "NORMAL").toUpperCase();
  return (TRACKER_PRIORITIES.find((p) => p.value === v) || TRACKER_PRIORITIES[0]).label;
}

function outlookPriorityBubble(value, htmlEscape) {
  const critical = String(value || "NORMAL").toUpperCase() === "CRITICAL";
  const label = critical ? "Critical" : "Normal";
  const bg = critical ? "#fef2f2" : "#f3f4f6";
  const color = critical ? "#b91c1c" : "#374151";
  const border = critical ? "#fecaca" : "#e5e7eb";
  return `<span style="display:inline-block;padding:2px 10px;border-radius:999px;font-size:11px;font-weight:600;background:${bg};color:${color};border:1px solid ${border};white-space:nowrap;">${htmlEscape(label)}</span>`;
}

export function chronologyHtmlForPrint(history, htmlEscape) {
  const items = sortHistoryRecentFirst(history).filter(
    (h) => String(h?.text || "").trim() || String(h?.date || "").trim()
  );
  if (!items.length) return "—";
  return items
    .map((h) => {
      const rawDate = String(h.date || "").trim();
      const label = rawDate ? formatDdMmmYy(rawDate) || rawDate : "";
      const today = Boolean(label && isChronologyToday(rawDate));
      const color = today ? "#1d4ed8" : "#374151";
      const weight = today ? "font-weight:bold;" : "";
      const dateBit = label
        ? `<span style="font-family:Consolas,monospace;">${htmlEscape(label)}</span>`
        : "";
      const text = String(h.text || "").trim();
      const body = text ? htmlEscape(text).replace(/\n/g, "<br>") : "";
      return `<div style="font-size:11px;color:${color};${weight}margin:2px 0;line-height:1.35;">${
        dateBit ? `${dateBit} ` : ""
      }${body}</div>`;
    })
    .join("");
}

/** L · Meeting minutes for Outlook/.eml / print — same table as the UI (no Status column). */
export function buildMinutesOutlookHtml(project, htmlEscape, outlookSectionTitle) {
  if (!project?.minutes?.length) return "";
  const parts = [outlookSectionTitle("L · Meeting minutes")];
  const openMinutes = project.minutes.filter((m) => {
    const closed = String(m.status || "open").toLowerCase() === "closed"
      || String(m._tracker?.status || "").toUpperCase() === "CLOSED";
    return !closed;
  });
  const rows = openMinutes.length ? openMinutes : project.minutes;
  const sorted = [...rows].sort((a, b) => compareItemNumbers(a.number, b.number));

  const groups = [];
  const groupIndex = new Map();
  for (const minute of sorted) {
    const key = minute.principalTopic || "General";
    if (!groupIndex.has(key)) {
      groupIndex.set(key, groups.length);
      const sec =
        String(minute.sectionNumber || "").trim() ||
        String(minute.number || "").trim().match(/^(\d+)/)?.[1] ||
        "";
      groups.push({ principal: key, sectionNumber: sec, minutes: [] });
    }
    groups[groupIndex.get(key)].minutes.push(minute);
  }

  const th = (label) =>
    `<th style="border:1px solid #d8dbe0;padding:6px 8px;font-family:Calibri,Arial,sans-serif;font-size:11px;background:#c00000;color:#ffffff;text-align:left;text-transform:uppercase;">${htmlEscape(label)}</th>`;
  const td = (inner, extra = "") =>
    `<td style="border:1px solid #d8dbe0;padding:6px 8px;font-family:Calibri,Arial,sans-serif;font-size:12px;vertical-align:top;background:#ffffff;color:#1e1f24;${extra}">${inner}</td>`;

  parts.push(
    `<table role="presentation" cellpadding="0" cellspacing="0" style="border-collapse:collapse;width:100%;margin:12px 0;">`,
    `<thead><tr>${th("Item")}${th("Topic")}${th("Chronology")}${th("Next action")}${th("Responsible")}${th("Target")}${th("Priority")}</tr></thead>`,
    `<tbody>`,
  );

  for (const g of groups) {
    const head = g.sectionNumber ? `${g.sectionNumber} · ${g.principal}` : g.principal;
    parts.push(
      `<tr><td colspan="7" style="border:1px solid #d8dbe0;padding:7px 10px;background:#e2e8f0;font-family:Calibri,Arial,sans-serif;font-size:12px;font-weight:bold;color:#334155;">${htmlEscape(head)}</td></tr>`,
    );
    for (const minute of g.minutes) {
      const tr = minute._tracker || {};
      const topic = minute.subTopic || "";
      const action = String(tr.nextAction || "").trim();
      const responsible = String(tr.nextActionBy || minute.owner || "").trim();
      const targetRaw = String(tr.targetDate || minute.targetUpdated || "").trim();
      const target = targetRaw ? formatDdMmmYy(targetRaw) || targetRaw : "—";
      parts.push(
        `<tr>`,
        td(htmlEscape(minute.number || ""), "white-space:nowrap;"),
        td(htmlEscape(topic || "—")),
        td(chronologyHtmlForPrint(tr.history, htmlEscape)),
        td(action ? htmlEscape(action).replace(/\n/g, "<br>") : "—"),
        td(htmlEscape(responsible || "—"), "white-space:nowrap;"),
        td(htmlEscape(target), "white-space:nowrap;"),
        td(outlookPriorityBubble(tr.priority, htmlEscape), "white-space:nowrap;"),
        `</tr>`,
      );
    }
  }

  parts.push(`</tbody></table>`);
  return parts.join("\n");
}

/**
 * Notes often embed an "Action:" line that is also stored on `_tracker.nextAction`.
 * Keep one copy: body without Action lines, then the red Action line.
 */
function minuteNoteAndActionForEmail(minute) {
  const rawNote =
    sortCommentsNewestFirst(minute.comments)[0]?.text
    || minute._tracker?.history?.slice(-1)?.[0]?.text
    || "";
  let action = String(minute._tracker?.nextAction || "").trim();
  const extracted = [];
  const kept = [];
  for (const line of String(rawNote).split("\n")) {
    const m = String(line).match(/^\s*Action:\s*(.*)$/i);
    if (m) {
      if (m[1].trim()) extracted.push(m[1].trim());
    } else {
      kept.push(line);
    }
  }
  let note = kept.join("\n").replace(/\n{3,}/g, "\n\n").trim();
  if (!action && extracted.length) action = extracted.join(" ");
  if (action && note) {
    const actNorm = action.replace(/\s+/g, " ").trim().toLowerCase();
    note = note
      .split(/\n\n+/)
      .filter((p) => p.replace(/\s+/g, " ").trim().toLowerCase() !== actNorm)
      .join("\n\n")
      .trim();
  }
  return { note, action };
}

export async function loadSurveillanceCampaigns(operatorId, fetchJson, apiUrl) {
  if (!operatorId || !fetchJson || !apiUrl) return [];
  try {
    const resp = await fetchJson(apiUrl(`/api/surveillances?operatorId=${operatorId}`));
    const list = Array.isArray(resp?.surveillances) ? resp.surveillances : [];
    const campaigns = [];
    for (const summary of list) {
      try {
        const detail = await fetchJson(apiUrl(`/api/surveillances/${summary.id}`));
        if (detail?.ok) {
          campaigns.push({ summary, surveillance: detail.surveillance, aircraft: detail.aircraft || [] });
        } else {
          campaigns.push({ summary, aircraft: [] });
        }
      } catch {
        campaigns.push({ summary, aircraft: [] });
      }
    }
    return campaigns;
  } catch {
    return [];
  }
}

/** @deprecated use loadSurveillanceCampaigns */
export async function loadOpenSurveillances(operatorId, fetchJson, apiUrl) {
  const campaigns = await loadSurveillanceCampaigns(operatorId, fetchJson, apiUrl);
  return campaigns.map((c) => c.summary);
}
